// @ts-nocheck
import Chart from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import Hammer from "hammerjs";
import { marked } from "marked";
import DOMPurify from "dompurify";

if (typeof window !== "undefined") {
  window.Hammer = Hammer;
}
Chart.register(zoomPlugin);
Object.assign(globalThis, { marked, DOMPurify });

const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom", labels: { color: "#c5d0de" } },
    tooltip: {
      bodyColor: "#e7ecf3",
      titleColor: "#c5d0de",
      backgroundColor: "#1a2332",
      borderColor: "#2a3545",
      borderWidth: 1
    }
  },
  scales: {
    x: { ticks: { color: "#8b9cb3", maxRotation: 45, minRotation: 0 }, grid: { color: "#2a3545" } },
    y: { ticks: { color: "#8b9cb3" }, grid: { color: "#2a3545" } }
  }
};

let atlasChart = null;
let atlasState = null;
let atlasToolbarWired = false;
const ATLAS_3D_POINT_SIZE = 0.52;

let atlas3dCtx = null;
let atlas3dPickScratch = null;
let atlas3dPointerBound = false;
let atlas3dHoverBound = false;
let atlas3dCircleTex = null;

let atlasTimelineRaf = null;
let atlasTimelinePulseChartRaf = null;
let atlasTimelineSpotlightInterval = null;
let atlasTimelineWired = false;
const ATLAS_TL_DURATION_1X_MS = 100000;
const ATLAS_TL_SPOTLIGHT_MS = 3000;
const ATLAS_TL_PULSE_MS = 900;
const ATLAS_TL_DIM = 0.12;
const ATLAS_TL_PEAK = 0.95;
const ATLAS_TL_TAU_SEC = 14 * 24 * 3600;

let atlasTimelineState = {
  engaged: false,
  running: false,
  simT: 0,
  tMin: 0,
  tMax: 0,
  speed: 1,
  lastWallMs: 0,
  firstTs: null,
  baseCss: null,
  baseA: null,
  spotlightVisible: null,
  spotlightLastSimT: -Infinity,
  pulseUntil: null
};

function getAtlas3dCircleTexture(THREE) {
  if (atlas3dCircleTex) return atlas3dCircleTex;
  const sz = 64;
  const canvas = document.createElement("canvas");
  canvas.width = sz;
  canvas.height = sz;
  const g = canvas.getContext("2d");
  const grd = g.createRadialGradient(
    sz / 2,
    sz / 2,
    0,
    sz / 2,
    sz / 2,
    sz / 2
  );
  grd.addColorStop(0, "rgba(255,255,255,1)");
  grd.addColorStop(0.5, "rgba(255,255,255,0.35)");
  grd.addColorStop(0.88, "rgba(255,255,255,0)");
  grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd;
  g.fillRect(0, 0, sz, sz);
  const tex = new THREE.CanvasTexture(canvas);
  if ("SRGBColorSpace" in THREE) tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  atlas3dCircleTex = tex;
  return tex;
}

function atlas3dPerVertexSizes(visible) {
  const n = visible.length;
  const out = new Float32Array(n);
  const counts = visible.map((p) =>
    typeof p.message_count === "number" && p.message_count > 0 ? p.message_count : 1
  );
  const minC = Math.min(...counts);
  const maxC = Math.max(...counts);
  const rRef = 14;
  const maxPixels = 22;
  const scaleToWorld = ATLAS_3D_POINT_SIZE / rRef;
  for (let i = 0; i < n; i++) {
    const c = counts[i];
    const t = maxC > minC ? Math.sqrt((c - minC) / (maxC - minC)) : 0.5;
    const px = Math.min(maxPixels, rRef * (0.48 + 1.35 * t));
    out[i] = px * scaleToWorld;
  }
  return out;
}

function createAtlas3dPointsMaterial(THREE, tex) {
  return new THREE.ShaderMaterial({
    uniforms: {
      pointTexture: { value: tex },
      uScale: { value: 1.0 }
    },
    vertexShader: `
      attribute float aPointSize;
      attribute float aOpacity;
      varying vec3 vColor;
      varying float vOpacity;
      uniform float uScale;
      void main() {
        vColor = color;
        vOpacity = aOpacity;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        float z = max(-mvPosition.z, 1e-4);
        gl_PointSize = clamp(aPointSize * uScale / z, 1.0, 2048.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D pointTexture;
      varying vec3 vColor;
      varying float vOpacity;
      void main() {
        vec4 texColor = texture2D(pointTexture, gl_PointCoord);
        if (texColor.a < 0.08) discard;
        gl_FragColor = vec4(vColor * texColor.rgb, texColor.a * vOpacity);
      }
    `,
    transparent: true,
    depthWrite: false,
    vertexColors: true
  });
}

function atlasHelp() {
  if (getApiBase()) {
    return "The API returned an unexpected response. Try signing in again, or open the API /api/health in a browser.";
  }
  try {
    const h = location.hostname || "";
    if (h.includes("vercel.app")) {
      return "This site loads data from the backend API. Set Vercel env USAGE_DASHBOARD_API_BASE to https://usage-api.samu.space (or redeploy; production builds default it).";
    }
  } catch (_) {
    /* ignore */
  }
  return "Local: cd 07-tools/conversations/usage-dashboard, run make data && make serve-api, open http://localhost:8765/";
}

const USAGE_DASH_TOKEN_KEY = "usage_dashboard_jwt";
const MEMORIAL_SESSION_KEY = "usage_dashboard_memorial_session";
let chatMessages = [];
let pendingLocationMap = null;
let currentSessionId = null;
let _memorialChatInited = false;
let canIngest = false;
let canRawUpload = false;
let isOwner = false;
let viewerEmail = null;

const USAGE_STATS_OWNER_EMAIL = "samubalogh@gmail.com";

function shouldShowUsageStatsGrid(email) {
  return (
    typeof email === "string" &&
    email.trim().toLowerCase() === USAGE_STATS_OWNER_EMAIL
  );
}

function getApiBase() {
  const fromEnv =
    typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE != null &&
    String(import.meta.env.VITE_API_BASE).trim() !== "";
  if (fromEnv) {
    return String(import.meta.env.VITE_API_BASE).trim().replace(/\/$/, "");
  }
  return String(
    typeof window !== "undefined" && window.__API_BASE__ != null
      ? window.__API_BASE__
      : ""
  )
    .trim()
    .replace(/\/$/, "");
}

function apiUrl(path) {
  const b = getApiBase();
  if (!b) return path.startsWith("/") ? path : "/" + path;
  const p = path.startsWith("/") ? path : "/" + path;
  return b + p;
}

async function apiFetch(path, opts = {}) {
  const headers = Object.assign({}, opts.headers || {});
  const t = sessionStorage.getItem(USAGE_DASH_TOKEN_KEY);
  if (t) headers["Authorization"] = "Bearer " + t;
  return fetch(apiUrl(path), Object.assign({}, opts, { headers }));
}

function consumeAuthHash() {
  const h = window.location.hash || "";
  if (!h.includes("access_token=")) return;
  const m = h.match(/access_token=([^&]+)/);
  if (m && m[1]) {
    try {
      sessionStorage.setItem(USAGE_DASH_TOKEN_KEY, decodeURIComponent(m[1]));
    } catch (_) {
      /* ignore */
    }
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

function isBrowserLoopbackHost() {
  const h = window.location.hostname || "";
  return h === "127.0.0.1" || h === "localhost" || h === "::1";
}

async function tryLocalBootstrap() {
  if (!getApiBase() || sessionStorage.getItem(USAGE_DASH_TOKEN_KEY)) return;
  if (!isBrowserLoopbackHost()) return;
  try {
    const res = await fetch(apiUrl("/api/local/bootstrap"));
    if (!res.ok) return;
    const j = await res.json().catch(() => ({}));
    if (j.access_token) {
      sessionStorage.setItem(USAGE_DASH_TOKEN_KEY, j.access_token);
    }
  } catch (_) {
    /* ignore */
  }
}

function authGateVisible() {
  return getApiBase() && !sessionStorage.getItem(USAGE_DASH_TOKEN_KEY);
}

function wireAuthGate() {
  const gate = document.getElementById("auth-gate");
  const form = document.getElementById("auth-form");
  const msg = document.getElementById("auth-msg");
  const signOut = document.getElementById("sign-out-btn");
  if (!form || !gate || !msg) return;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = (document.getElementById("auth-email") || {}).value || "";
    msg.textContent = "Sending…";
    try {
      const res = await fetch(apiUrl("/auth/request"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = j.detail;
        let errMsg = "Request failed";
        if (typeof d === "string") errMsg = d;
        else if (Array.isArray(d)) errMsg = d.map((x) => x.msg || String(x)).join(", ");
        else if (d) errMsg = JSON.stringify(d);
        throw new Error(errMsg);
      }
      msg.textContent = j.sent
        ? "Check your email for the link."
        : "If that address is allowed, you will receive an email.";
    } catch (err) {
      msg.textContent = String(err.message || err);
    }
  });
  if (signOut) {
    signOut.addEventListener("click", () => {
      sessionStorage.removeItem(USAGE_DASH_TOKEN_KEY);
      _memorialChatInited = false;
      window.location.reload();
    });
  }
}

function updateSignOutVisibility() {
  const btn = document.getElementById("sign-out-btn");
  if (btn) btn.style.display = getApiBase() && sessionStorage.getItem(USAGE_DASH_TOKEN_KEY) ? "inline-block" : "none";
}

function formatUploadBytes(n) {
  const x = Number(n) || 0;
  if (x < 1024) return x + " B";
  if (x < 1048576) return (x / 1024).toFixed(1) + " KB";
  return (x / 1048576).toFixed(1) + " MB";
}

function formatUploadMtime(ts) {
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch (_) {
    return "";
  }
}

function renderStoredUploadsTable(files) {
  const wrap = document.getElementById("rag-uploads-table-wrap");
  if (!wrap) return;
  wrap.textContent = "";
  if (!files || !files.length) {
    const p = document.createElement("p");
    p.className = "atlas-note";
    p.textContent = "No raw uploads in your folder yet.";
    wrap.appendChild(p);
    return;
  }
  const table = document.createElement("table");
  const thead = document.createElement("thead");
  const hr = document.createElement("tr");
  for (const lab of ["Name", "Size", "Modified", ""]) {
    const th = document.createElement("th");
    th.textContent = lab;
    hr.appendChild(th);
  }
  thead.appendChild(hr);
  table.appendChild(thead);
  const tb = document.createElement("tbody");
  for (const f of files) {
    const tr = document.createElement("tr");
    tr.className = "rag-upload-row-open";
    tr.title = "Open in viewer";
    const sp = f.stored_path != null ? String(f.stored_path) : "";
    const fn = f.filename != null ? String(f.filename) : "";
    const openFile = () => {
      if (sp) void openStoredUploadModal(sp, fn);
    };
    tr.addEventListener("click", (ev) => {
      if (ev.target.closest("button")) return;
      openFile();
    });
    const td1 = document.createElement("td");
    td1.textContent = fn;
    const td2 = document.createElement("td");
    td2.textContent = formatUploadBytes(f.size_bytes);
    const td3 = document.createElement("td");
    td3.textContent = formatUploadMtime(f.mtime);
    const td4 = document.createElement("td");
    const ob = document.createElement("button");
    ob.type = "button";
    ob.className = "atlas-reset-btn";
    ob.textContent = "Open";
    ob.addEventListener("click", (e) => {
      e.stopPropagation();
      openFile();
    });
    td4.appendChild(ob);
    tr.appendChild(td1);
    tr.appendChild(td2);
    tr.appendChild(td3);
    tr.appendChild(td4);
    tb.appendChild(tr);
  }
  table.appendChild(tb);
  wrap.appendChild(table);
}

async function refreshStoredUploadsList() {
  const st = document.getElementById("rag-uploads-status");
  const wrap = document.getElementById("rag-uploads-table-wrap");
  if (!canRawUpload || !getApiBase() || !sessionStorage.getItem(USAGE_DASH_TOKEN_KEY)) {
    if (wrap) wrap.textContent = "";
    return;
  }
  if (st) st.textContent = "Loading…";
  try {
    const res = await apiFetch("/api/uploads");
    const j = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (st) st.textContent = res.status === 403 ? "Not permitted." : "Could not load list.";
      if (wrap) wrap.textContent = "";
      return;
    }
    const files = j.files || [];
    if (st) st.textContent = files.length ? "" : "No files yet.";
    renderStoredUploadsTable(files);
  } catch (e) {
    if (st) st.textContent = String(e.message || e);
    if (wrap) wrap.textContent = "";
  }
}

function updateIngestPanelVisibility() {
  const admin = document.getElementById("rag-admin-details");
  const lb = document.getElementById("login-allowlist-block");
  const p = document.getElementById("rag-ingest-panel");
  const pu = document.getElementById("rag-uploads-panel");
  const fr = document.getElementById("rag-ingest-file-row");
  const tr = document.getElementById("rag-ingest-text-row");
  const ur = document.getElementById("rag-ingest-url-row");
  const intro = document.getElementById("rag-ingest-intro");
  const show =
    (isOwner || canIngest || canRawUpload) &&
    getApiBase() &&
    sessionStorage.getItem(USAGE_DASH_TOKEN_KEY) &&
    !authGateVisible();
  const showIngest = show && (canIngest || canRawUpload);
  if (fr) fr.style.display = canRawUpload ? "" : "none";
  if (tr) tr.style.display = canRawUpload ? "" : "none";
  if (ur) ur.style.display = canIngest ? "" : "none";
  if (intro) {
    if (canRawUpload && canIngest) {
      intro.textContent =
        "Upload ChatGPT JSON, text, PDF, or images. Or ingest a page URL.";
    } else if (canIngest) {
      intro.textContent = "Ingest a page URL to add it to the archive.";
    } else if (canRawUpload) {
      intro.textContent = "Upload ChatGPT JSON, text, PDF, or images.";
    }
  }
  if (lb) lb.style.display = show && isOwner ? "block" : "none";
  if (admin) admin.style.display = show ? "block" : "none";
  if (p) p.style.display = showIngest ? "block" : "none";
  if (pu) {
    pu.style.display = showIngest && canRawUpload ? "block" : "none";
    if (showIngest && canRawUpload) void refreshStoredUploadsList();
  }
}

async function refreshMe() {
  if (!getApiBase() || !sessionStorage.getItem(USAGE_DASH_TOKEN_KEY)) {
    viewerEmail = null;
    canIngest = false;
    canRawUpload = false;
    isOwner = false;
    updateIngestPanelVisibility();
    return;
  }
  try {
    const res = await apiFetch("/api/me");
    if (!res.ok) {
      viewerEmail = null;
      canIngest = false;
      canRawUpload = false;
      isOwner = false;
      updateIngestPanelVisibility();
      return;
    }
    const j = await res.json();
    viewerEmail = j.email != null ? String(j.email) : null;
    canIngest = !!j.can_ingest;
    canRawUpload = !!j.can_raw_upload;
    isOwner = !!j.is_owner;
    updateIngestPanelVisibility();
    if (isOwner) void loadLoginAllowlist();
  } catch (_) {
    viewerEmail = null;
    canIngest = false;
    canRawUpload = false;
    isOwner = false;
    updateIngestPanelVisibility();
  }
}

function updateChatSectionVisibility() {
  const sec = document.getElementById("section-chat");
  if (!sec) return;
  if (!getApiBase()) {
    sec.style.display = "block";
    canIngest = false;
    canRawUpload = false;
    isOwner = false;
    updateIngestPanelVisibility();
    return;
  }
  const vis = !authGateVisible();
  sec.style.display = vis ? "block" : "none";
  if (vis) {
    if (!_memorialChatInited) {
      _memorialChatInited = true;
      void initMemorialChatSession();
    } else {
      void refreshChatSessions();
    }
    void refreshMe();
  } else {
    canIngest = false;
    canRawUpload = false;
    isOwner = false;
    updateIngestPanelVisibility();
  }
}

async function loadLoginAllowlist() {
  const ta = document.getElementById("login-allowlist-ta");
  const st = document.getElementById("login-allowlist-status");
  if (!isOwner || !getApiBase() || !ta) return;
  if (st) st.textContent = "Loading…";
  try {
    const res = await apiFetch("/api/admin/login-allowlist");
    const j = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (st) st.textContent = res.status === 403 ? "Not permitted." : "Could not load.";
      return;
    }
    const rows = j.emails_file || [];
    ta.value = Array.isArray(rows) ? rows.join("\n") : "";
    const envN = Array.isArray(j.emails_env) ? j.emails_env.length : 0;
    const effN = Array.isArray(j.emails_effective) ? j.emails_effective.length : 0;
    if (st) {
      st.textContent =
        envN > 0
          ? `Saved ${rows.length} in file · ${envN} from env · ${effN} can sign in total.`
          : `Saved ${rows.length} in file · ${effN} can sign in total.`;
    }
  } catch (e) {
    if (st) st.textContent = String(e.message || e);
  }
}

function wireLoginAllowlist() {
  const save = document.getElementById("login-allowlist-save");
  const reload = document.getElementById("login-allowlist-reload");
  const ta = document.getElementById("login-allowlist-ta");
  const st = document.getElementById("login-allowlist-status");
  if (!save || !ta) return;
  if (reload) {
    reload.addEventListener("click", () => {
      void loadLoginAllowlist();
    });
  }
  save.addEventListener("click", () => {
    void (async () => {
      if (!isOwner) return;
      const lines = String(ta.value || "")
        .split(/\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (st) st.textContent = "Saving…";
      try {
        const res = await apiFetch("/api/admin/login-allowlist", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ emails: lines })
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) {
          const d = j.detail;
          if (st) st.textContent = typeof d === "string" ? d : JSON.stringify(d || res.status);
          return;
        }
        const n = Array.isArray(j.emails_file) ? j.emails_file.length : lines.length;
        if (st) st.textContent = "Saved (" + n + " in file).";
        ta.value = (j.emails_file || []).join("\n");
      } catch (e) {
        if (st) st.textContent = String(e.message || e);
      }
    })();
  });
}

function hideChatHeatmapBtn() {
  pendingLocationMap = null;
  const b = document.getElementById("chat-heatmap-btn");
  if (b) b.style.display = "none";
}

function setChatHeatmapFromMeta(locMap) {
  const b = document.getElementById("chat-heatmap-btn");
  if (!b || !locMap || !locMap.start || !locMap.end) {
    hideChatHeatmapBtn();
    return;
  }
  pendingLocationMap = locMap;
  b.style.display = "inline-block";
  const lab = locMap.label ? " (" + String(locMap.label).slice(0, 56) + ")" : "";
  b.textContent = "Open location heatmap" + lab;
}

async function openPendingLocationHeatmap() {
  const map = pendingLocationMap;
  if (!map || !map.start || !map.end) return;
  const backdropId = "location-heatmap-modal-backdrop";
  const old = document.getElementById(backdropId);
  if (old) old.remove();
  const backdrop = document.createElement("div");
  backdrop.id = backdropId;
  backdrop.className = "location-heatmap-backdrop";
  backdrop.innerHTML =
    '<div class="location-heatmap-panel" role="dialog" aria-modal="true" aria-label="Location heatmap">' +
    '<div class="location-heatmap-head">' +
    '<span class="location-heatmap-title">Timeline heatmap</span>' +
    '<button type="button" class="location-heatmap-close atlas-reset-btn" aria-label="Close">Close</button>' +
    "</div>" +
    '<div id="location-heatmap-map" class="location-heatmap-map"></div>' +
    '<p class="location-heatmap-foot" id="location-heatmap-status"></p>' +
    "</div>";
  document.body.appendChild(backdrop);
  const stEl = backdrop.querySelector("#location-heatmap-status");
  const mapDiv = backdrop.querySelector("#location-heatmap-map");
  const closeBtn = backdrop.querySelector(".location-heatmap-close");
  const clean = () => {
    try {
      if (backdrop._leafletMap) backdrop._leafletMap.remove();
    } catch (_) {
      /* ignore */
    }
    backdrop.remove();
  };
  closeBtn.addEventListener("click", clean);
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) clean();
  });
  try {
    if (stEl) stEl.textContent = "Loading…";
    const leafletMod = await import("leaflet");
    const L = leafletMod.default;
    if (typeof window !== "undefined") window.L = L;
    await import("leaflet/dist/leaflet.css");
    await import("leaflet.heat");
    const url =
      "/api/location/heatmap?start=" +
      encodeURIComponent(String(map.start)) +
      "&end=" +
      encodeURIComponent(String(map.end));
    const res = await apiFetch(url);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = data.detail;
      if (stEl) stEl.textContent = typeof d === "string" ? d : JSON.stringify(d || res.status);
      return;
    }
    const pts = Array.isArray(data.points) ? data.points : [];
    if (!pts.length) {
      if (stEl) stEl.textContent = "No points in this range.";
      return;
    }
    if (stEl) stEl.textContent = data.range_label ? String(data.range_label) : "";
    const m = L.map(mapDiv, { attributionControl: true, scrollWheelZoom: true }).setView(
      [pts[0][0], pts[0][1]],
      10
    );
    backdrop._leafletMap = m;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors"
    }).addTo(m);
    const heatPts = pts.map((p) => [
      p[0],
      p[1],
      typeof p[2] === "number" && !Number.isNaN(p[2]) ? p[2] : 0.5
    ]);
    const maxW = Math.max(...heatPts.map((x) => x[2]), 0.01);
    const heatLayer = L.heatLayer(heatPts, {
      radius: 24,
      blur: 12,
      maxZoom: 16,
      max: maxW,
      minOpacity: 0.4,
      gradient: {
        0.15: "rgba(32, 58, 108, 0.45)",
        0.35: "rgba(20, 48, 92, 0.72)",
        0.55: "rgba(12, 36, 74, 0.88)",
        0.75: "rgba(8, 26, 56, 0.94)",
        0.92: "rgba(5, 18, 42, 0.98)",
        1: "rgba(2, 10, 28, 1)"
      }
    });
    heatLayer.addTo(m);
    if (data.bounds && Array.isArray(data.bounds) && data.bounds.length === 2) {
      m.fitBounds(data.bounds, { padding: [24, 24] });
    } else {
      m.fitBounds(L.latLngBounds(heatPts.map((p) => [p[0], p[1]])), { padding: [24, 24] });
    }
    requestAnimationFrame(() => m.invalidateSize());
    setTimeout(() => m.invalidateSize(), 220);
  } catch (e) {
    if (stEl) stEl.textContent = e && e.message ? e.message : String(e);
  }
}

function memorialApplySessionId(sid) {
  if (!sid) return;
  currentSessionId = sid;
  try {
    sessionStorage.setItem(MEMORIAL_SESSION_KEY, sid);
  } catch (_) {
    /* ignore */
  }
}

async function refreshChatSessions() {
  const tb = document.getElementById("chat-toolbar");
  if (!tb) return;
  if (!getApiBase() || !sessionStorage.getItem(USAGE_DASH_TOKEN_KEY)) {
    tb.style.display = "none";
    return;
  }
  tb.style.display = "flex";
  try {
    const res = await apiFetch("/api/chat/sessions");
    if (!res.ok) return;
    const data = await res.json();
    const sel = document.getElementById("chat-session-select");
    if (!sel) return;
    const keep = currentSessionId;
    sel.innerHTML = "";
    const opt0 = document.createElement("option");
    opt0.value = "";
    opt0.textContent = "New conversation…";
    sel.appendChild(opt0);
    for (const s of data.sessions || []) {
      const o = document.createElement("option");
      o.value = s.id;
      o.textContent = String(s.title || s.id).slice(0, 72);
      sel.appendChild(o);
    }
    if (keep && [...sel.options].some((o) => o.value === keep)) sel.value = keep;
    else sel.value = "";
  } catch (_) {
    /* ignore */
  }
}

async function loadSessionMessages(sid) {
  hideChatHeatmapBtn();
  const res = await apiFetch("/api/chat/sessions/" + encodeURIComponent(sid));
  if (!res.ok) return;
  const data = await res.json();
  chatMessages = (data.messages || []).map((m) => ({
    role: m.role,
    content: m.content
  }));
  memorialApplySessionId(sid);
  clearChatSuggestedReplies();
  revealChatSuggestionsPanel(false);
  renderChatLog();
  const sel = document.getElementById("chat-session-select");
  if (sel) sel.value = sid;
}

function newChatSession() {
  hideChatHeatmapBtn();
  clearChatSuggestedReplies();
  revealChatSuggestionsPanel(false);
  chatMessages = [];
  currentSessionId = null;
  try {
    sessionStorage.removeItem(MEMORIAL_SESSION_KEY);
  } catch (_) {
    /* ignore */
  }
  renderChatLog();
  const sel = document.getElementById("chat-session-select");
  if (sel) sel.value = "";
}

async function initMemorialChatSession() {
  if (!getApiBase() || !sessionStorage.getItem(USAGE_DASH_TOKEN_KEY)) return;
  await refreshChatSessions();
  const mid = sessionStorage.getItem(MEMORIAL_SESSION_KEY);
  if (mid) {
    const sel = document.getElementById("chat-session-select");
    if (sel && [...sel.options].some((o) => o.value === mid)) {
      sel.value = mid;
      await loadSessionMessages(mid);
    }
  }
}

function wireChatToolbar() {
  const sel = document.getElementById("chat-session-select");
  const bn = document.getElementById("chat-new");
  const bd = document.getElementById("chat-delete");
  if (sel) {
    sel.addEventListener("change", () => {
      const v = sel.value;
      if (!v) newChatSession();
      else void loadSessionMessages(v);
    });
  }
  if (bn) bn.addEventListener("click", () => {
    newChatSession();
    void refreshChatSessions();
  });
  if (bd) {
    bd.addEventListener("click", () => {
      if (!currentSessionId) return;
      if (!confirm("Delete this conversation?")) return;
      void (async () => {
        const res = await apiFetch(
          "/api/chat/sessions/" + encodeURIComponent(currentSessionId),
          { method: "DELETE" }
        );
        if (res.ok) {
          newChatSession();
          await refreshChatSessions();
        }
      })();
    });
  }
  const hm = document.getElementById("chat-heatmap-btn");
  if (hm) {
    hm.addEventListener("click", () => {
      void openPendingLocationHeatmap();
    });
  }
}

function chatMdToHtml(src) {
  const s = String(src || "");
  if (typeof marked === "undefined" || typeof DOMPurify === "undefined") {
    return escHtml(s);
  }
  try {
    const html = marked.parse(s, { breaks: true, async: false });
    return DOMPurify.sanitize(html);
  } catch (_) {
    return escHtml(s);
  }
}

const CHAT_DEFAULT_THINKING_LABEL = "Gathering data from conversations…";

const MEMORIAL_EXAMPLE_QUESTIONS = [
  "What themes show up around relationships and attachment?",
  "Where did he spend time in 2025 and what do his chats add?",
  "How does he talk about work, stress, and downtime?",
  "What does the archive reflect about his emotional life, in his words?"
];

function dismissChatSuggestionsPanel() {
  const el = document.getElementById("chat-prompt-rows");
  if (el) el.classList.add("chat-prompt-rows--dismissed");
}

function revealChatSuggestionsPanel(animate) {
  const el = document.getElementById("chat-prompt-rows");
  if (!el) return;
  el.classList.remove("chat-prompt-rows--dismissed");
  if (!animate) {
    showChatExampleChips();
    return;
  }
  el.classList.remove("chat-prompt-rows--anim-in");
  void el.offsetWidth;
  el.classList.add("chat-prompt-rows--anim-in");
  el.addEventListener(
    "animationend",
    () => el.classList.remove("chat-prompt-rows--anim-in"),
    { once: true }
  );
}

function hideChatExampleChips() {
  const wrap = document.getElementById("chat-unified-chips");
  if (!wrap) return;
  wrap.querySelectorAll(".chat-chip--example").forEach((el) => {
    el.hidden = true;
  });
}

function showChatExampleChips() {
  const wrap = document.getElementById("chat-unified-chips");
  if (!wrap) return;
  wrap.querySelectorAll(".chat-chip--example").forEach((el) => {
    el.hidden = false;
  });
}

function clearChatSuggestedReplies() {
  const wrap = document.getElementById("chat-unified-chips");
  if (!wrap) return;
  wrap.querySelectorAll(".chat-chip--followup").forEach((el) => el.remove());
}

function setChatSuggestedReplies(items) {
  const wrap = document.getElementById("chat-unified-chips");
  if (!wrap) return;
  clearChatSuggestedReplies();
  const xs = Array.isArray(items)
    ? items.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  const cap = xs.slice(0, 6);
  if (cap.length > 0) {
    hideChatExampleChips();
    for (let i = 0; i < cap.length; i++) {
      const t = cap[i];
      const b = document.createElement("button");
      b.type = "button";
      b.style.setProperty("--chat-chip-delay", `${i * 48}ms`);
      b.className = "chat-chip chat-chip--followup chat-chip--popin";
      b.setAttribute("role", "listitem");
      b.textContent = t;
      b.addEventListener("click", () => {
        const input = document.getElementById("chat-input");
        if (input) {
          input.value = t;
          input.focus();
        }
        void sendChat();
      });
      wrap.appendChild(b);
    }
  } else {
    showChatExampleChips();
  }
  revealChatSuggestionsPanel(true);
}

function wireMemorialExampleChips() {
  const list = document.getElementById("chat-unified-chips");
  if (!list || list.dataset.wired === "1") return;
  list.dataset.wired = "1";
  for (const t of MEMORIAL_EXAMPLE_QUESTIONS) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "chat-chip chat-chip--example";
    b.setAttribute("role", "listitem");
    b.textContent = t;
    b.addEventListener("click", () => {
      const input = document.getElementById("chat-input");
      if (input) {
        input.value = t;
        input.focus();
      }
      void sendChat();
    });
    list.appendChild(b);
  }
}

function renderChatMessages(opts) {
  const partialAssistant = opts && opts.partialAssistant != null ? opts.partialAssistant : null;
  const thinking = opts && opts.thinking;
  const thinkingLabel =
    opts && opts.thinkingLabel != null && String(opts.thinkingLabel).trim()
      ? String(opts.thinkingLabel)
      : CHAT_DEFAULT_THINKING_LABEL;
  const el = document.getElementById("chat-log");
  if (!el) return;
  const lockScroll =
    thinking || partialAssistant !== null || (opts && opts.preserveScroll);
  const prevScrollTop = el.scrollTop;
  el.replaceChildren();
  for (const m of chatMessages) {
    const d = document.createElement("div");
    const isUser = m.role === "user";
    const body = isUser ? escHtml(m.content) : chatMdToHtml(m.content);
    const cls = isUser ? "chat-msg-user" : "chat-msg-md";
    if (isUser) {
      d.className = "chat-turn chat-turn--user";
      d.innerHTML =
        '<div class="chat-turn-main"><div class="chat-bubble chat-bubble--user"><div class="chat-msg-body ' +
        cls +
        '">' +
        body +
        '</div></div></div><div class="chat-avatar chat-avatar--user" aria-hidden="true">U</div>';
    } else {
      d.className = "chat-turn chat-turn--assistant";
      d.innerHTML =
        '<div class="chat-avatar chat-avatar--assistant" aria-hidden="true">A</div><div class="chat-turn-main"><div class="chat-bubble chat-bubble--assistant"><div class="chat-msg-body ' +
        cls +
        '">' +
        body +
        "</div></div></div>";
    }
    el.appendChild(d);
  }
  if (thinking) {
    const d = document.createElement("div");
    d.className = "chat-turn chat-turn--assistant";
    d.innerHTML =
      '<div class="chat-avatar chat-avatar--assistant" aria-hidden="true">A</div><div class="chat-turn-main"><div class="chat-bubble chat-bubble--assistant chat-bubble--thinking"><span class="chat-thinking" aria-live="polite"><span class="chat-spinner" aria-hidden="true"></span> ' +
      escHtml(thinkingLabel) +
      "</span></div></div>";
    el.appendChild(d);
  } else if (partialAssistant !== null) {
    const d = document.createElement("div");
    d.className = "chat-turn chat-turn--assistant";
    d.innerHTML =
      '<div class="chat-avatar chat-avatar--assistant" aria-hidden="true">A</div><div class="chat-turn-main"><div class="chat-bubble chat-bubble--assistant"><div class="chat-msg-body chat-msg-md">' +
      chatMdToHtml(partialAssistant) +
      "</div></div></div>";
    el.appendChild(d);
  }
  if (lockScroll) {
    const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
    el.scrollTop = Math.min(Math.max(0, prevScrollTop), maxTop);
  } else {
    el.scrollTop = el.scrollHeight;
  }
}

function renderChatLog(opts) {
  renderChatMessages(opts || {});
}

function renderChatThinking(label) {
  renderChatMessages({
    thinking: true,
    thinkingLabel: label != null && String(label).trim() ? String(label) : CHAT_DEFAULT_THINKING_LABEL
  });
}

function renderChatLogStreaming(partialAssistant) {
  renderChatMessages({ partialAssistant });
}

async function sendChat() {
  const input = document.getElementById("chat-input");
  const status = document.getElementById("chat-status");
  const sendBtn = document.getElementById("chat-send");
  const text = (input && input.value ? input.value : "").trim();
  if (!text) return;
  if (getApiBase() && !sessionStorage.getItem(USAGE_DASH_TOKEN_KEY)) {
    if (status) status.textContent = "Sign in first.";
    return;
  }
  dismissChatSuggestionsPanel();
  clearChatSuggestedReplies();
  showChatExampleChips();
  hideChatHeatmapBtn();
  input.value = "";
  chatMessages.push({ role: "user", content: text });
  renderChatThinking();
  if (status) status.textContent = "";
  if (sendBtn) sendBtn.disabled = true;
  const headers = { "Content-Type": "application/json" };
  const tok = sessionStorage.getItem(USAGE_DASH_TOKEN_KEY);
  if (tok) headers["Authorization"] = "Bearer " + tok;
  const chatPayload = { messages: chatMessages };
  if (currentSessionId) chatPayload.session_id = currentSessionId;
  let res;
  try {
    res = await fetch(apiUrl("/api/chat"), {
      method: "POST",
      headers,
      body: JSON.stringify(chatPayload),
      cache: "no-store"
    });
  } catch (e) {
    const msg = e && e.message ? e.message : String(e);
    if (status) {
      status.textContent =
        msg === "Load failed" || msg === "Failed to fetch"
          ? "Network error (try again; on iOS/Safari long streams sometimes drop)."
          : msg;
    }
    renderChatLog();
    if (sendBtn) sendBtn.disabled = false;
    revealChatSuggestionsPanel(false);
    return;
  }
  if (!res.ok) {
    if (status) status.textContent = "HTTP " + res.status;
    renderChatLog();
    if (sendBtn) sendBtn.disabled = false;
    revealChatSuggestionsPanel(false);
    return;
  }
  if (!res.body) {
    if (status) status.textContent = "Empty response body.";
    renderChatLog();
    if (sendBtn) sendBtn.disabled = false;
    revealChatSuggestionsPanel(false);
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buffer = "";
  let assistant = "";
  let receivedSuggestedRepliesMeta = false;
  if (status) status.textContent = "";
  try {
    while (true) {
      let chunk;
      try {
        chunk = await reader.read();
      } catch (readErr) {
        const rm = readErr && readErr.message ? readErr.message : String(readErr);
        if (status) {
          status.textContent = assistant
            ? "Stream interrupted (" + rm + ")."
            : "Stream failed: " + rm;
        }
        if (assistant) {
          chatMessages.push({ role: "assistant", content: assistant });
          renderChatLog({ preserveScroll: true });
        }
        revealChatSuggestionsPanel(false);
        return;
      }
      const { done, value } = chunk;
      if (done) break;
      buffer += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line) continue;
        let j;
        try {
          j = JSON.parse(line);
        } catch {
          continue;
        }
        if (j.progress && j.progress.label) {
          renderChatThinking(String(j.progress.label));
        }
        if (j.error) {
          if (j.session_id) memorialApplySessionId(j.session_id);
          if (status) {
            status.textContent =
              j.error === "rate_limited"
                ? "Rate limited. Wait a moment."
                : j.error === "missing_user_message"
                  ? "Missing user message."
                  : j.detail || j.error;
          }
          chatMessages.push({ role: "assistant", content: assistant || "(error)" });
          renderChatLog({ preserveScroll: true });
          if (sendBtn) sendBtn.disabled = false;
          void refreshChatSessions();
          revealChatSuggestionsPanel(false);
          return;
        }
        if (j.meta) {
          if (j.meta.session_id) memorialApplySessionId(j.meta.session_id);
          if (j.meta.retrieval_query) {
            void applyAtlasSemanticSearch(String(j.meta.retrieval_query), {
              updateInput: true
            });
          }
          if (j.meta.location_map) {
            setChatHeatmapFromMeta(j.meta.location_map);
            renderChatThinking("Drafting reply using your location data…");
          }
          if ("suggested_replies" in j.meta && Array.isArray(j.meta.suggested_replies)) {
            receivedSuggestedRepliesMeta = true;
            setChatSuggestedReplies(j.meta.suggested_replies);
          }
        }
        if (j.c) {
          assistant += j.c;
          renderChatLogStreaming(assistant);
        }
      }
    }
    if (buffer.trim()) {
      try {
        const j = JSON.parse(buffer.trim());
        if (j.c) assistant += j.c;
        if (j.error && status) status.textContent = j.detail || j.error;
      } catch {
        /* ignore */
      }
    }
    chatMessages.push({ role: "assistant", content: assistant });
    renderChatLog({ preserveScroll: true });
    if (!receivedSuggestedRepliesMeta) {
      showChatExampleChips();
      revealChatSuggestionsPanel(true);
    }
  } catch (e) {
    const msg = e && e.message ? e.message : String(e);
    if (status) status.textContent = "Chat error: " + msg;
    if (assistant) {
      chatMessages.push({ role: "assistant", content: assistant });
      renderChatLog({ preserveScroll: true });
    }
    revealChatSuggestionsPanel(false);
  } finally {
    if (sendBtn) sendBtn.disabled = false;
    void refreshChatSessions();
    try {
      await reader.cancel();
    } catch (_) {
      /* ignore */
    }
  }
}

function wireRagIngest() {
  const st = document.getElementById("rag-ingest-status");
  const btnFile = document.getElementById("rag-ingest-upload-btn");
  const inp = document.getElementById("rag-ingest-file");
  const btnText = document.getElementById("rag-ingest-text-btn");
  const taText = document.getElementById("rag-ingest-text");
  const titleInp = document.getElementById("rag-ingest-text-label-inp");
  const btnUrl = document.getElementById("rag-ingest-url-btn");
  const urlIn = document.getElementById("rag-ingest-url");
  const ser = document.getElementById("rag-ingest-serper");
  if (btnFile && inp) {
    btnFile.addEventListener("click", () => {
      void (async () => {
        if (!inp.files || !inp.files[0]) {
          if (st) st.textContent = "Choose a file first.";
          return;
        }
        if (st) st.textContent = "Uploading…";
        const fd = new FormData();
        fd.append("file", inp.files[0], inp.files[0].name);
        try {
          const res = await fetch(apiUrl("/api/ingest"), {
            method: "POST",
            headers: (() => {
              const h = {};
              const t = sessionStorage.getItem(USAGE_DASH_TOKEN_KEY);
              if (t) h["Authorization"] = "Bearer " + t;
              return h;
            })(),
            body: fd
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok) {
            const d = j.detail;
            if (st) st.textContent = typeof d === "string" ? d : JSON.stringify(d || res.status);
            return;
          }
          if (st) st.textContent = "Embedded: " + (j.embedded ?? "?") + ", skipped: " + (j.skipped ?? "?");
          if (canRawUpload) void refreshStoredUploadsList();
        } catch (e) {
          if (st) st.textContent = String(e.message || e);
        }
      })();
    });
  }
  if (btnText && taText) {
    btnText.addEventListener("click", () => {
      void (async () => {
        const text = (taText.value || "").trim();
        if (!text) {
          if (st) st.textContent = "Paste some text first.";
          return;
        }
        if (st) st.textContent = "Embedding…";
        const title = titleInp ? (titleInp.value || "").trim() : "";
        try {
          const res = await apiFetch("/api/ingest/text", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              text,
              title: title || undefined
            })
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok) {
            const d = j.detail;
            if (st) st.textContent = typeof d === "string" ? d : JSON.stringify(d || res.status);
            return;
          }
          if (st) st.textContent = "Embedded: " + (j.embedded ?? "?") + ", skipped: " + (j.skipped ?? "?");
          taText.value = "";
          if (titleInp) titleInp.value = "";
          if (canRawUpload) void refreshStoredUploadsList();
        } catch (e) {
          if (st) st.textContent = String(e.message || e);
        }
      })();
    });
  }
  const btnUp = document.getElementById("rag-uploads-refresh-btn");
  if (btnUp) {
    btnUp.addEventListener("click", () => void refreshStoredUploadsList());
  }
  if (btnUrl && urlIn) {
    btnUrl.addEventListener("click", () => {
      void (async () => {
        const u = (urlIn.value || "").trim();
        if (!u) {
          if (st) st.textContent = "Enter a URL.";
          return;
        }
        if (st) st.textContent = "Fetching…";
        try {
          const res = await apiFetch("/api/ingest/url", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url: u,
              max_chars: 120000,
              use_serper: !!(ser && ser.checked)
            })
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok) {
            const d = j.detail;
            if (st) st.textContent = typeof d === "string" ? d : JSON.stringify(d || res.status);
            return;
          }
          if (st) st.textContent = "Embedded: " + (j.embedded ?? "?") + ", skipped: " + (j.skipped ?? "?");
          if (canRawUpload) void refreshStoredUploadsList();
        } catch (e) {
          if (st) st.textContent = String(e.message || e);
        }
      })();
    });
  }
}

function wireChat() {
  wireMemorialExampleChips();
  wireChatToolbar();
  wireRagIngest();
  wireLoginAllowlist();
  const btn = document.getElementById("chat-send");
  const ta = document.getElementById("chat-input");
  if (!btn || !ta) return;
  btn.addEventListener("click", () => void sendChat());
  ta.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendChat();
    }
  });
}

function convexHull2D(pts) {
  if (pts.length < 2) return pts.slice();
  const sorted = pts
    .slice()
    .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  const cross = (o, a, b) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function clusterHullStroke(clusterId) {
  const h = ((clusterId | 0) * 47) % 360;
  return `hsla(${h}, 26%, 58%, 0.32)`;
}

function chaikinClosed(ring, iterations) {
  if (ring.length < 3) return ring;
  let pts = ring.map(([x, y]) => ({ x, y }));
  for (let iter = 0; iter < iterations; iter++) {
    const next = [];
    const n = pts.length;
    for (let i = 0; i < n; i++) {
      const p0 = pts[i];
      const p1 = pts[(i + 1) % n];
      next.push({
        x: 0.75 * p0.x + 0.25 * p1.x,
        y: 0.75 * p0.y + 0.25 * p1.y
      });
      next.push({
        x: 0.25 * p0.x + 0.75 * p1.x,
        y: 0.25 * p0.y + 0.75 * p1.y
      });
    }
    pts = next;
  }
  return pts.map((p) => [p.x, p.y]);
}

function bulgeControlPoint(a, b) {
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  const k = len * 0.16;
  return [mx + (-dy / len) * k, my + (dx / len) * k];
}

function sampleQuadraticBezier(a, b, c, steps) {
  const ring = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    const x = u * u * a[0] + 2 * u * t * c[0] + t * t * b[0];
    const y = u * u * a[1] + 2 * u * t * c[1] + t * t * b[1];
    ring.push([x, y]);
  }
  return ring;
}

function clusterPointCentroid(pts) {
  let sx = 0;
  let sy = 0;
  for (const p of pts) {
    sx += p.x;
    sy += p.y;
  }
  const n = pts.length;
  return { x: sx / n, y: sy / n };
}

function clusterSpanXY(pts) {
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  return Math.max(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
    1e-12
  );
}

function padRingOutward(ring, cx, cy, pad) {
  return ring.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const len = Math.hypot(dx, dy) || 1;
    return [x + (dx / len) * pad, y + (dy / len) * pad];
  });
}

function buildClusterHullRings(points, viewBounds) {
  const viewSpan =
    viewBounds &&
    Math.max(
      viewBounds.xMax - viewBounds.xMin,
      viewBounds.yMax - viewBounds.yMin,
      1e-12
    );
  const byId = new Map();
  for (const p of points) {
    if (typeof p.cluster_id !== "number") continue;
    const id = p.cluster_id;
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push({ x: p.x, y: p.y });
  }
  const rings = [];
  for (const [clusterId, pts] of byId) {
    const span = clusterSpanXY(pts);
    const cen = clusterPointCentroid(pts);
    const pad = Math.max(span * 0.11, viewSpan ? viewSpan * 0.016 : 0);
    if (pts.length >= 3) {
      const h = convexHull2D(pts);
      const raw = h.map((q) => [q.x, q.y]);
      let ring = chaikinClosed(raw, 2);
      ring = padRingOutward(ring, cen.x, cen.y, pad);
      rings.push({
        clusterId,
        ring,
        mode: "polygon"
      });
    } else if (pts.length === 2) {
      const a = [pts[0].x, pts[0].y];
      const b = [pts[1].x, pts[1].y];
      const c = bulgeControlPoint(a, b);
      let ring = sampleQuadraticBezier(a, b, c, 28);
      ring = padRingOutward(ring, cen.x, cen.y, pad);
      rings.push({
        clusterId,
        ring,
        mode: "strip"
      });
    }
  }
  return rings;
}

Chart.register({
  id: "atlasClusterHulls",
  beforeDatasetsDraw(chart) {
    if (!chart.atlasShowHulls || !chart.atlasHullRings || !chart.atlasHullRings.length) {
      return;
    }
    const sx = chart.scales.x;
    const sy = chart.scales.y;
    if (!sx || !sy) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    for (const item of chart.atlasHullRings) {
      const { clusterId, ring, mode } = item;
      if (!ring || ring.length < 2) continue;
      const stroke = clusterHullStroke(clusterId);
      ctx.beginPath();
      for (let i = 0; i < ring.length; i++) {
        const px = sx.getPixelForValue(ring[i][0]);
        const py = sy.getPixelForValue(ring[i][1]);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      if (mode === "polygon") {
        ctx.closePath();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 0.9;
        ctx.stroke();
      } else if (mode === "strip") {
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    ctx.restore();
  }
});

Chart.register({
  id: "atlasTimelinePulseRings",
  afterDatasetsDraw(chart) {
    if (!atlasTimelineState.engaged || !atlasTimelineState.pulseUntil) return;
    if (chart.config.type !== "scatter") return;
    const ds = chart.data.datasets[0];
    if (!ds || !ds.data || !ds.data.length) return;
    const n = ds.data.length;
    const pu = atlasTimelineState.pulseUntil;
    if (pu.length !== n) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.hidden) return;
    const now = performance.now();
    const P = ATLAS_TL_PULSE_MS;
    const ctx = chart.ctx;
    ctx.save();
    for (let i = 0; i < n; i++) {
      const until = pu[i];
      if (until <= now) continue;
      const el = meta.data[i];
      if (!el || el.skip) continue;
      const x = el.x;
      const y = el.y;
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const t = 1 - (until - now) / P;
      const rOuter = 4 + t * 26;
      const alpha = (1 - t) * 0.62;
      ctx.beginPath();
      ctx.arc(x, y, rOuter, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(96, 210, 255, ${alpha})`;
      ctx.lineWidth = 2.2;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, rOuter * 0.4, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.38})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    ctx.restore();
  }
});

function fmtDate(ts) {
  if (ts == null) return "—";
  const d = new Date(ts * 1000);
  return d.toISOString().slice(0, 10);
}

function fmtUtcDateTime(ts) {
  if (ts == null || !Number.isFinite(Number(ts))) return "—";
  const n = Number(ts);
  if (n <= 0) return "—";
  const d = new Date(n * 1000);
  return `${new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  }).format(d)} UTC`;
}

function fmtAtlasFirstTsHuman(ts) {
  if (ts == null || !Number.isFinite(Number(ts))) return "Date unknown";
  const n = Number(ts);
  if (n <= 0) return "Date unknown";
  const d = new Date(n * 1000);
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(d);
  } catch (_) {
    return d.toLocaleString();
  }
}

function escHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

function atlasPointTooltipLines(p) {
  if (!p) return [];
  let regionLine = "";
  if (typeof p.cluster_id === "number" && atlasState) {
    const lab = atlasState.clusterLabels[String(p.cluster_id)];
    const s = lab ? String(lab) : "";
    regionLine = s
      ? `Region: ${s.length > 130 ? s.slice(0, 130) + "…" : s}`
      : `Region ${p.cluster_id + 1}`;
  }
  return [
    (p.title || "").slice(0, 140) || "(no title)",
    p.year != null ? `Year: ${p.year}` : "Year: unknown",
    typeof p.message_count === "number" ? `Messages: ${p.message_count}` : "",
    regionLine
  ].filter(Boolean);
}

function hideAtlas3dTooltip() {
  const el = document.getElementById("atlas-3d-tooltip");
  if (!el) return;
  el.style.display = "none";
  el.innerHTML = "";
  el.setAttribute("aria-hidden", "true");
  if (atlas3dCtx) atlas3dCtx.lastHoverIdx = -1;
}

function positionAtlas3dTooltip(tip, ev) {
  const pad = 14;
  const w = tip.offsetWidth;
  const h = tip.offsetHeight;
  let left = ev.clientX + pad;
  let top = ev.clientY + pad;
  if (left + w > window.innerWidth - 8) left = ev.clientX - w - pad;
  if (top + h > window.innerHeight - 8) top = ev.clientY - h - pad;
  left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
  top = Math.max(8, Math.min(top, window.innerHeight - h - 8));
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

function formatMedianGapBetweenChats(days) {
  if (days == null || days === "") return "—";
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1 / 1440) return "under a minute";
  if (n < 1) {
    const h = n * 24;
    const s = h >= 10 ? Math.round(h) : Math.round(h * 10) / 10;
    return `about ${s} hours`;
  }
  if (n < 30) {
    const rounded = n >= 10 ? Math.round(n) : Math.round(n * 10) / 10;
    const unit = rounded === 1 ? "day" : "days";
    return `about ${rounded} ${unit}`;
  }
  const w = n / 7;
  const rounded = w >= 10 ? Math.round(w) : Math.round(w * 10) / 10;
  const unit = rounded === 1 ? "week" : "weeks";
  return `about ${rounded} ${unit}`;
}

function escAttr(s) {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function fmtMsgTime(ts) {
  if (ts == null) return "";
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toISOString().replace("T", " ").slice(0, 19)} UTC`;
}

function stripCitationArtifacts(text) {
  return String(text)
    .replace(/[\uE000-\uF8FF]/g, "")
    .replace(/[\u200B-\u200D\uFEFF\u2060]/g, "")
    .replace(/\uFFFD/g, "")
    .replace(/cite\s*turn\d+view\d+/gi, "");
}

function messageContentToHtml(raw) {
  const cleaned = stripCitationArtifacts(raw || "");
  if (typeof marked === "undefined" || typeof DOMPurify === "undefined") {
    return escHtml(cleaned);
  }
  try {
    const opts = { breaks: true, gfm: true };
    const html =
      typeof marked.parse === "function"
        ? marked.parse(cleaned, opts)
        : marked(cleaned, opts);
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  } catch {
    return escHtml(cleaned);
  }
}

function renderConversationMessages(messages) {
  if (!messages || !messages.length) {
    return '<p class="atlas-note">No user or assistant messages.</p>';
  }
  return messages
    .map((m) => {
      const role = m.role === "user" ? "user" : "assistant";
      const label = role === "user" ? "You" : "Assistant";
      const t = fmtMsgTime(m.create_time);
      const body = messageContentToHtml(m.content);
      return `<div class="conv-msg conv-msg-${role}">
    <div class="conv-msg-role">${escHtml(label)}</div>
    <div class="conv-msg-bubble conv-msg-md">${body}</div>
    ${t ? `<div class="conv-msg-time">${escHtml(t)}</div>` : ""}
  </div>`;
    })
    .join("");
}

async function openStoredUploadModal(storedPath, displayName) {
  const dlg = document.getElementById("conv-dialog");
  const titleEl = document.getElementById("conv-dialog-title");
  const bodyEl = document.getElementById("conv-dialog-body");
  titleEl.textContent = displayName || "File";
  bodyEl.innerHTML = '<p class="atlas-note">Loading…</p>';
  dlg.showModal();
  try {
    const res = await apiFetch(
      "/api/uploads/content?stored_path=" + encodeURIComponent(storedPath)
    );
    const j = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = j.detail;
      throw new Error(typeof d === "string" ? d : "HTTP " + res.status);
    }
    let text = j.text != null ? String(j.text) : "";
    const lower = (displayName || "").toLowerCase();
    if (lower.endsWith(".json")) {
      try {
        text = JSON.stringify(JSON.parse(text), null, 2);
      } catch (_) {
        /* keep decoded utf-8 */
      }
    }
    const pre = document.createElement("pre");
    pre.className = "raw-upload-pre";
    pre.textContent = text;
    bodyEl.replaceChildren(pre);
    if (j.truncated) {
      const note = document.createElement("p");
      note.className = "atlas-note";
      const shown = j.shown_bytes != null ? j.shown_bytes : text.length;
      const total = j.size_bytes != null ? j.size_bytes : shown;
      note.textContent =
        "Showing first " +
        shown.toLocaleString() +
        " of " +
        total.toLocaleString() +
        " bytes.";
      bodyEl.appendChild(note);
    }
  } catch (e) {
    titleEl.textContent = "Could not load file";
    bodyEl.innerHTML =
      '<p class="conv-dialog-err">' +
      escHtml(String(e.message || e)) +
      "</p>";
  }
}

async function openConversationModal(conversationId) {
  const dlg = document.getElementById("conv-dialog");
  const titleEl = document.getElementById("conv-dialog-title");
  const bodyEl = document.getElementById("conv-dialog-body");
  titleEl.textContent = "Loading…";
  bodyEl.innerHTML = '<p class="atlas-note">Loading…</p>';
  dlg.showModal();
  try {
    const res = await apiFetch(
      `/api/conversation?conversation_id=${encodeURIComponent(conversationId)}`
    );
    const raw = await res.text();
    let j;
    try {
      j = JSON.parse(raw);
    } catch {
      throw new Error(atlasHelp());
    }
    if (!j.ok) throw new Error(j.error || `HTTP ${res.status}`);
    titleEl.textContent = j.title || "(no title)";
    bodyEl.innerHTML = renderConversationMessages(j.messages);
  } catch (e) {
    titleEl.textContent = "Could not load thread";
    bodyEl.innerHTML = `<p class="conv-dialog-err">${escHtml(String(e.message || e))}</p><p class="atlas-note">${escHtml(atlasHelp())}</p>`;
  }
}

function wireConversationDialog() {
  const dlg = document.getElementById("conv-dialog");
  document.getElementById("conv-dialog-close").addEventListener("click", () => dlg.close());
  dlg.addEventListener("click", (e) => {
    if (e.target === dlg) dlg.close();
  });
}

function renderLongestChatHero(rows) {
  const wrap = document.getElementById("longest-chat-hero");
  const inner = document.getElementById("longest-chat-hero-inner");
  if (!wrap || !inner) return;
  if (!rows || !rows.length) {
    wrap.style.display = "none";
    inner.replaceChildren();
    return;
  }
  const row = rows[0];
  wrap.style.display = "block";
  inner.replaceChildren();
  const cid = row.conversation_id || "";
  const meta = document.createElement("p");
  meta.className = "longest-chat-meta";
  const tc = Number(row.total_chars);
  meta.textContent = [
    `${row.message_count} messages`,
    `${Number.isFinite(tc) ? tc.toLocaleString() : row.total_chars} chars`
  ].join(" · ");
  const dates = document.createElement("p");
  dates.className = "longest-chat-dates";
  dates.textContent = `Started ${fmtUtcDateTime(row.first_ts)} · Ended ${fmtUtcDateTime(row.last_ts)}`;
  inner.append(meta, dates);
  if (cid) {
    const actions = document.createElement("p");
    actions.className = "longest-chat-actions";
    const openBtn = document.createElement("button");
    openBtn.type = "button";
    openBtn.className = "atlas-reset-btn";
    openBtn.textContent = "Open full thread";
    openBtn.title = "Same modal as atlas point click";
    openBtn.addEventListener("click", () => openConversationModal(cid));
    actions.appendChild(openBtn);
    inner.appendChild(actions);
  }
}

function addCards(summary) {
  const el = document.getElementById("summary-cards");
  const items = [
    {
      label: "Conversations",
      val: summary.conversations,
      tip: "Threads in this export that have at least one message after cleaning."
    },
    {
      label: "Total messages",
      val: summary.total_messages,
      tip: "All messages in the export (all threads combined)."
    },
    {
      label: "Median msgs / chat",
      val: summary.median_messages_per_conversation,
      tip: "Half of conversations have at most this many messages per thread."
    },
    {
      label: "P90 msgs / chat",
      val: summary.p90_messages_per_conversation,
      tip: "90th percentile of messages per conversation: most threads are this size or smaller."
    },
    {
      label: "Longest single message (chars)",
      val: summary.longest_message_chars,
      tip: "Largest character count of any single message in the export (see data.json summary.longest_message_conversation_title for which chat)."
    },
    {
      label: "Typical time between new chats",
      val: formatMedianGapBetweenChats(summary.median_gap_days_between_new_conversations),
      tip: "Sort chats by when they started. This is the middle value of the gaps between the first message of one chat and the first message of the next."
    }
  ];
  el.innerHTML = items
    .map(
      ({ label, val, tip }) =>
        `<div class="card" title="${escAttr(tip)}"><div class="label">${escHtml(label)}</div><div class="val">${escHtml(val)}</div></div>`
    )
    .join("");
}

function colorForYear(year, minY, maxY) {
  if (year == null) return "rgba(150, 150, 160, 0.55)";
  const t = (year - minY) / (maxY - minY || 1);
  const h = 200 + t * 120;
  return `hsla(${h}, 62%, 52%, 0.72)`;
}

function clamp01(x) {
  return Math.min(1, Math.max(0, x));
}

function buildAtlasSearchStrengthMap(results) {
  const m = new Map();
  if (!results || !results.length) return m;
  const n = results.length;

  function setMax(cid, val) {
    if (!cid) return;
    const v = clamp01(val);
    const prev = m.get(cid);
    if (prev == null || v > prev) m.set(cid, v);
  }

  const rrfs = results.filter(
    (r) => typeof r.rrf === "number" && Number.isFinite(r.rrf)
  );
  if (rrfs.length === n && n > 0) {
    const vals = rrfs.map((r) => r.rrf);
    const minR = Math.min(...vals);
    const maxR = Math.max(...vals);
    const span = maxR - minR || 1;
    for (const r of results) {
      setMax(r.conversation_id, (r.rrf - minR) / span);
    }
    return m;
  }

  const withD = results.filter(
    (r) => typeof r.distance === "number" && Number.isFinite(r.distance)
  );
  const withB = results.filter(
    (r) => typeof r.bm25 === "number" && Number.isFinite(r.bm25)
  );
  let dMin;
  let dMax;
  let bMin;
  let bMax;
  if (withD.length) {
    const ds = withD.map((r) => r.distance);
    dMin = Math.min(...ds);
    dMax = Math.max(...ds);
  }
  if (withB.length) {
    const bs = withB.map((r) => r.bm25);
    bMin = Math.min(...bs);
    bMax = Math.max(...bs);
  }

  function invDist(d) {
    if (dMax === dMin) return 1;
    return clamp01(1 - (d - dMin) / (dMax - dMin));
  }
  function invBm(b) {
    if (bMax === bMin) return 1;
    return clamp01(1 - (b - bMin) / (bMax - bMin));
  }

  for (let i = 0; i < n; i++) {
    const r = results[i];
    const cid = r.conversation_id;
    const parts = [];
    if (
      typeof r.distance === "number" &&
      Number.isFinite(r.distance) &&
      withD.length
    ) {
      parts.push(invDist(r.distance));
    }
    if (typeof r.bm25 === "number" && Number.isFinite(r.bm25) && withB.length) {
      parts.push(invBm(r.bm25));
    }
    let s;
    if (parts.length) {
      s = parts.reduce((a, b) => a + b, 0) / parts.length;
    } else {
      s = n <= 1 ? 1 : 1 - i / (n - 1);
    }
    setMax(cid, s);
  }
  return m;
}

function atlasSearchHitColor(strength) {
  const t = typeof strength === "number" ? clamp01(strength) : 0.65;
  const rr = Math.round(175 + 80 * t);
  const gg = Math.round(92 + 118 * t);
  const bb = Math.round(38 + 95 * t);
  const aa = 0.38 + 0.58 * t;
  return `rgba(${rr},${gg},${bb},${aa})`;
}

function atlasSearchBoostForConversationId(cid) {
  const st = atlasState && atlasState.searchStrength;
  if (!st || !cid) return 1;
  const s = st.get(cid);
  if (s == null) return 1;
  return 0.86 + 0.4 * clamp01(s);
}

let atlasCssColorProbeCtx = null;

function atlasRgbaFromCss(css) {
  if (!atlasCssColorProbeCtx) {
    const c = document.createElement("canvas");
    c.width = 1;
    c.height = 1;
    atlasCssColorProbeCtx = c.getContext("2d", { willReadFrequently: true });
  }
  const ctx = atlasCssColorProbeCtx;
  ctx.fillStyle = "#000000";
  ctx.fillStyle = css;
  const s = String(ctx.fillStyle);
  if (s.startsWith("#")) {
    let hex = s.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }
    return {
      r: parseInt(hex.slice(0, 2), 16) / 255,
      g: parseInt(hex.slice(2, 4), 16) / 255,
      b: parseInt(hex.slice(4, 6), 16) / 255,
      a: 1
    };
  }
  const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/.exec(
    s
  );
  if (m) {
    return {
      r: parseInt(m[1], 10) / 255,
      g: parseInt(m[2], 10) / 255,
      b: parseInt(m[3], 10) / 255,
      a: m[4] != null ? parseFloat(m[4]) : 1
    };
  }
  return { r: 0.5, g: 0.5, b: 0.55, a: 1 };
}

function atlasColorRangeForAtlasView() {
  const st = atlasState;
  if (
    st.has3d &&
    st.allPoints &&
    st.allPoints3d &&
    st.allPoints.length === st.allPoints3d.length
  ) {
    const a = getAtlasVisiblePoints();
    const b = getAtlasVisiblePoints3d();
    const ys = [
      ...a.map((p) => p.year).filter((y) => y != null),
      ...b.map((p) => p.year).filter((y) => y != null)
    ];
    if (!ys.length) return { min: 0, max: 1 };
    return { min: Math.min(...ys), max: Math.max(...ys) };
  }
  const vis =
    st.mode === "3d" && st.has3d ? getAtlasVisiblePoints3d() : getAtlasVisiblePoints();
  return atlasColorRange(vis);
}

function applyAtlasSearchHitExclusion(chart) {
  const ds = chart.data.datasets[0];
  const pts = chart.atlasVisiblePoints;
  const ids = atlasState && atlasState.searchIds;
  if (!ds || !pts || pts.length !== ds.data.length || !ds.data.length) return;
  if (!ids || ids.size === 0) return;
  const n = pts.length;
  const hr = ds.pointHoverRadius;
  const hit = ds.pointHitRadius;
  const hoverArr = Array.isArray(hr) ? hr.slice() : Array.from({ length: n }, () => hr);
  const hitArr = Array.isArray(hit) ? hit.slice() : Array.from({ length: n }, () => hit);
  for (let i = 0; i < n; i++) {
    if (!ids.has(pts[i].conversation_id)) {
      hoverArr[i] = 0;
      hitArr[i] = 0;
    }
  }
  ds.pointHoverRadius = hoverArr;
  ds.pointHitRadius = hitArr;
}

function atlas3dConversationIsSearchHit(conversationId) {
  const ids = atlasState && atlasState.searchIds;
  if (!ids || ids.size === 0) return true;
  return ids.has(conversationId);
}

function ensureAtlas3dPickScratch(THREE) {
  if (!atlas3dPickScratch) {
    atlas3dPickScratch = {
      world: new THREE.Vector3(),
      mv: new THREE.Vector3(),
      ndc: new THREE.Vector3(),
      mvMat: new THREE.Matrix4()
    };
  }
  return atlas3dPickScratch;
}

function atlas3dPickScreenIndex(clientX, clientY, rect) {
  const ctx = atlas3dCtx;
  if (!ctx) return -1;
  const THREE = ctx.THREE;
  const { camera, points, conversationIds, renderer } = ctx;
  const geom = points.geometry;
  const posAttr = geom.attributes.position;
  const sizeAttr = geom.attributes.aPointSize;
  if (!posAttr || !sizeAttr) return -1;
  const n = posAttr.count;
  const mx = clientX - rect.left;
  const my = clientY - rect.top;
  const h = renderer.domElement.height;
  const pr = renderer.getPixelRatio();
  const uScale = h * 0.5 * pr;
  const pad = 10;
  const scratch = ensureAtlas3dPickScratch(THREE);
  const { world, mv, ndc, mvMat } = scratch;

  points.updateMatrixWorld(true);
  camera.updateMatrixWorld(true);
  mvMat.multiplyMatrices(camera.matrixWorldInverse, points.matrixWorld);

  let bestIdx = -1;
  let bestDist = Infinity;
  let bestDepth = Infinity;

  for (let i = 0; i < n; i++) {
    const cid = conversationIds[i];
    if (!cid || !atlas3dConversationIsSearchHit(cid)) continue;

    world.fromBufferAttribute(posAttr, i).applyMatrix4(points.matrixWorld);
    mv.copy(world).applyMatrix4(mvMat);
    if (mv.z >= 0) continue;

    const z = Math.max(-mv.z, 1e-4);
    const aPointSize = sizeAttr.array[i];
    const pointSizePx = Math.min(2048, Math.max(1, aPointSize * uScale / z));

    ndc.copy(world).project(camera);
    const sx = (ndc.x * 0.5 + 0.5) * rect.width;
    const sy = (-ndc.y * 0.5 + 0.5) * rect.height;
    const dist = Math.hypot(mx - sx, my - sy);
    const hitR = pointSizePx * 0.48 + pad;
    if (dist > hitR) continue;

    const depth = -mv.z;
    if (
      dist < bestDist - 1e-4 ||
      (Math.abs(dist - bestDist) <= 1e-4 && depth < bestDepth)
    ) {
      bestDist = dist;
      bestDepth = depth;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function atlas3dPickIndexFromPointer(ev, canvas) {
  const ctx = atlas3dCtx;
  if (!ctx || !ctx.points || !ctx.conversationIds.length) return -1;
  const rect = canvas.getBoundingClientRect();
  return atlas3dPickScreenIndex(ev.clientX, ev.clientY, rect);
}

function syncAtlasPointRadius(chart) {
  const b = chart.atlasDataBounds;
  const sx = chart.scales.x;
  const sy = chart.scales.y;
  const ds = chart.data.datasets[0];
  if (!b || !sx || !sy || !ds || !ds.data || ds.data.length === 0) return;
  const spanX = b.xMax - b.xMin || 1e-12;
  const spanY = b.yMax - b.yMin || 1e-12;
  const vx = Math.max(sx.max - sx.min, 1e-12);
  const vy = Math.max(sy.max - sy.min, 1e-12);
  const zx = spanX / vx;
  const zy = spanY / vy;
  const zoom = Math.sqrt(Math.max(0, zx) * Math.max(0, zy));
  const base = 5;
  const maxR = 22;
  const r = Math.min(maxR, Math.max(base, base * Math.sqrt(zoom)));
  const pts = chart.atlasVisiblePoints;
  const dn0 = ds.data.length;
  if (!pts || pts.length !== dn0) {
    ds.pointRadius = r;
    ds.pointHoverRadius = Math.min(32, r + 8);
    ds.pointHitRadius = Math.min(38, r + 10);
    applyAtlasSearchHitExclusion(chart);
    if (
      atlasTimelineState.engaged &&
      atlasTimelineState.pulseUntil &&
      atlasTimelineState.pulseUntil.length === dn0
    ) {
      atlasMergeTimelinePulseBorders(ds, dn0);
    } else {
      ds.pointBorderWidth = 0;
      ds.pointBorderColor = "transparent";
    }
    chart.update("none");
    return;
  }
  const counts = pts.map((p) =>
    typeof p.message_count === "number" && p.message_count > 0 ? p.message_count : 1
  );
  const minC = Math.min(...counts);
  const maxC = Math.max(...counts);
  const radii = counts.map((c) => {
    const t = maxC > minC ? Math.sqrt((c - minC) / (maxC - minC)) : 0.5;
    return Math.min(maxR, r * (0.48 + 1.35 * t));
  });
  if (atlasState.searchIds && atlasState.searchIds.size > 0 && atlasState.searchStrength) {
    for (let i = 0; i < radii.length; i++) {
      const cid = pts[i].conversation_id;
      if (atlasState.searchIds.has(cid)) {
        radii[i] *= atlasSearchBoostForConversationId(cid);
      }
    }
  }
  ds.pointRadius = radii;
  ds.pointHoverRadius = radii.map((x) => Math.min(38, x + 10));
  ds.pointHitRadius = radii.map((x) => Math.min(46, x + 12));
  applyAtlasSearchHitExclusion(chart);
  const dn = ds.data.length;
  if (
    atlasTimelineState.engaged &&
    atlasTimelineState.pulseUntil &&
    atlasTimelineState.pulseUntil.length === dn
  ) {
    atlasMergeTimelinePulseBorders(ds, dn);
  } else {
    ds.pointBorderWidth = 0;
    ds.pointBorderColor = "transparent";
  }
  chart.update("none");
}

function getAtlasVisiblePoints() {
  const all = atlasState.allPoints;
  let minY = parseInt(document.getElementById("atlas-y-min").value, 10);
  let maxY = parseInt(document.getElementById("atlas-y-max").value, 10);
  if (Number.isNaN(minY)) minY = atlasState.yearMin;
  if (Number.isNaN(maxY)) maxY = atlasState.yearMax;
  if (minY > maxY) {
    const t = minY;
    minY = maxY;
    maxY = t;
  }
  const unk = document.getElementById("atlas-unknown").checked;
  return all.filter((p) => {
    if (p.year == null) return unk;
    return p.year >= minY && p.year <= maxY;
  });
}

function getAtlasVisiblePoints3d() {
  const all = atlasState.allPoints3d;
  if (!all || !all.length) return [];
  let minY = parseInt(document.getElementById("atlas-y-min").value, 10);
  let maxY = parseInt(document.getElementById("atlas-y-max").value, 10);
  if (Number.isNaN(minY)) minY = atlasState.yearMin;
  if (Number.isNaN(maxY)) maxY = atlasState.yearMax;
  if (minY > maxY) {
    const t = minY;
    minY = maxY;
    maxY = t;
  }
  const unk = document.getElementById("atlas-unknown").checked;
  return all.filter((p) => {
    if (p.year == null) return unk;
    return p.year >= minY && p.year <= maxY;
  });
}

function atlasColorRange(points) {
  const ys = points.map((p) => p.year).filter((y) => y != null);
  if (!ys.length) return { min: 0, max: 1 };
  return { min: Math.min(...ys), max: Math.max(...ys) };
}

function atlasColorsForPoints(points, cMin, cMax) {
  const ids = atlasState.searchIds;
  const str = atlasState.searchStrength;
  if (ids && ids.size > 0) {
    return points.map((p) => {
      if (!ids.has(p.conversation_id)) {
        return "rgba(55, 70, 95, 0.22)";
      }
      const t = str && str.get(p.conversation_id);
      return atlasSearchHitColor(t);
    });
  }
  return points.map((p) => colorForYear(p.year, cMin, cMax));
}

function atlasYearColorsOnly(points, cMin, cMax) {
  return points.map((p) => colorForYear(p.year, cMin, cMax));
}

function atlasTimelineFirstTsValid(f) {
  return typeof f === "number" && Number.isFinite(f) && f > 0;
}

function atlasCssWithTimelineAlpha(css, tAlpha) {
  const { r, g, b, a } = atlasRgbaFromCss(css);
  const outA = Math.min(1, Math.max(0, tAlpha * a));
  return `rgba(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)},${outA})`;
}

function atlasTimelineAlphaAt(simT, f) {
  if (!atlasTimelineFirstTsValid(f)) {
    return ATLAS_TL_DIM;
  }
  if (simT < f) {
    return 0;
  }
  const x = simT - f;
  return Math.min(
    1,
    ATLAS_TL_DIM +
      (ATLAS_TL_PEAK - ATLAS_TL_DIM) * Math.exp(-x / ATLAS_TL_TAU_SEC)
  );
}

function cancelAtlasTimelinePulseChartRaf() {
  if (atlasTimelinePulseChartRaf != null) {
    cancelAnimationFrame(atlasTimelinePulseChartRaf);
    atlasTimelinePulseChartRaf = null;
  }
}

function atlasHasActiveTimelinePulse() {
  const pu = atlasTimelineState.pulseUntil;
  if (!pu || !pu.length) return false;
  const now = performance.now();
  for (let i = 0; i < pu.length; i++) {
    if (pu[i] > now) return true;
  }
  return false;
}

function atlasTimelinePulseChartStep() {
  atlasTimelinePulseChartRaf = null;
  if (!atlasChart || !atlasTimelineState.engaged || atlasTimelineState.running) return;
  if (!atlasState || atlasState.mode !== "2d") return;
  if (!atlasHasActiveTimelinePulse()) return;
  if (atlasChart.getActiveElements().length > 0) {
    atlasTimelinePulseChartRaf = requestAnimationFrame(atlasTimelinePulseChartStep);
    return;
  }
  const tip = atlasChart.tooltip;
  if (tip && typeof tip.opacity === "number" && tip.opacity > 0) {
    atlasTimelinePulseChartRaf = requestAnimationFrame(atlasTimelinePulseChartStep);
    return;
  }
  const ds = atlasChart.data.datasets[0];
  const n = ds?.data?.length || 0;
  if (!n || atlasTimelineState.pulseUntil?.length !== n) return;
  atlasMergeTimelinePulseBorders(ds, n);
  atlasChart.update("none");
  atlasTimelinePulseChartRaf = requestAnimationFrame(atlasTimelinePulseChartStep);
}

function atlasMaybeStartPulseChartRaf() {
  if (atlasTimelineState.running) return;
  if (!atlasChart || !atlasState || atlasState.mode !== "2d") return;
  if (!atlasHasActiveTimelinePulse()) return;
  if (atlasTimelinePulseChartRaf != null) return;
  atlasTimelinePulseChartRaf = requestAnimationFrame(atlasTimelinePulseChartStep);
}

function atlasMergeTimelinePulseBorders(ds, n) {
  const pu = atlasTimelineState.pulseUntil;
  if (!pu || pu.length !== n) {
    ds.pointBorderWidth = 0;
    ds.pointBorderColor = "transparent";
    return;
  }
  const now = performance.now();
  const P = ATLAS_TL_PULSE_MS;
  const borderW = [];
  const borderC = [];
  for (let i = 0; i < n; i++) {
    const until = pu[i];
    if (until > now) {
      const t = 1 - (until - now) / P;
      const env = Math.sin(t * Math.PI);
      borderW.push(1.2 + env * 3.8);
      borderC.push(`rgba(110, 220, 255, ${0.32 + env * 0.58})`);
    } else {
      borderW.push(0);
      borderC.push("rgba(0,0,0,0)");
    }
  }
  ds.pointBorderWidth = borderW;
  ds.pointBorderColor = borderC;
}

function registerAtlasTimelineAppearances(prevSimT, simT) {
  const fts = atlasTimelineState.firstTs;
  const pu = atlasTimelineState.pulseUntil;
  if (!fts || !pu || fts.length !== pu.length) return;
  let added = false;
  for (let i = 0; i < fts.length; i++) {
    const f = fts[i];
    if (!atlasTimelineFirstTsValid(f)) continue;
    if (prevSimT < f && f <= simT) {
      pu[i] = performance.now() + ATLAS_TL_PULSE_MS;
      added = true;
    }
  }
  if (added && !atlasTimelineState.running) atlasMaybeStartPulseChartRaf();
}

function cancelAtlasTimelineRaf() {
  if (atlasTimelineRaf != null) {
    cancelAnimationFrame(atlasTimelineRaf);
    atlasTimelineRaf = null;
  }
}

function stopAtlasTimelineSpotlightInterval() {
  if (atlasTimelineSpotlightInterval != null) {
    clearInterval(atlasTimelineSpotlightInterval);
    atlasTimelineSpotlightInterval = null;
  }
}

function clearAtlasTimelineSpotlightDom() {
  const w2 = document.getElementById("atlas-tl-spotlight-2d");
  const w3 = document.getElementById("atlas-tl-spotlight-3d");
  const b2 = document.getElementById("atlas-tl-spotlight-body-2d");
  const b3 = document.getElementById("atlas-tl-spotlight-body-3d");
  if (b2) b2.innerHTML = "";
  if (b3) b3.innerHTML = "";
  if (w2) w2.style.display = "none";
  if (w3) w3.style.display = "none";
}

function teardownAtlasTimelineSpotlight() {
  stopAtlasTimelineSpotlightInterval();
  clearAtlasTimelineSpotlightDom();
  cancelAtlasTimelinePulseChartRaf();
  atlasTimelineState.spotlightVisible = null;
  atlasTimelineState.spotlightLastSimT = -Infinity;
  atlasTimelineState.pulseUntil = null;
}

function startAtlasTimelineSpotlightInterval() {
  stopAtlasTimelineSpotlightInterval();
  atlasTimelineSpotlightInterval = setInterval(() => {
    if (!atlasTimelineState.running) return;
    tickAtlasTimelineSpotlightWindow();
  }, ATLAS_TL_SPOTLIGHT_MS);
}

function atlasTimelinePickBestInSimRange(simLo, simHi) {
  const pts = atlasTimelineState.spotlightVisible;
  const fts = atlasTimelineState.firstTs;
  if (!pts || !fts || pts.length !== fts.length) return null;
  let bestIdx = -1;
  let bestF = -Infinity;
  for (let i = 0; i < fts.length; i++) {
    const f = fts[i];
    if (!atlasTimelineFirstTsValid(f)) continue;
    if (f > simLo && f <= simHi && f >= bestF) {
      bestF = f;
      bestIdx = i;
    }
  }
  return bestIdx >= 0 ? pts[bestIdx] : null;
}

function atlasTimelinePickLatestAtOrBefore(simT) {
  const pts = atlasTimelineState.spotlightVisible;
  const fts = atlasTimelineState.firstTs;
  if (!pts || !fts || pts.length !== fts.length) return null;
  let bestIdx = -1;
  let bestF = -Infinity;
  for (let i = 0; i < fts.length; i++) {
    const f = fts[i];
    if (!atlasTimelineFirstTsValid(f)) continue;
    if (f <= simT && f >= bestF) {
      bestF = f;
      bestIdx = i;
    }
  }
  return bestIdx >= 0 ? pts[bestIdx] : null;
}

function renderAtlasTimelineSpotlightForPoint(p) {
  const w2 = document.getElementById("atlas-tl-spotlight-2d");
  const w3 = document.getElementById("atlas-tl-spotlight-3d");
  const b2 = document.getElementById("atlas-tl-spotlight-body-2d");
  const b3 = document.getElementById("atlas-tl-spotlight-body-3d");
  if (!p || !w2 || !w3 || !b2 || !b3) return;
  const title = ((p.title || "").trim() || "(no title)").slice(0, 200);
  const dateLine = atlasTimelineFirstTsValid(p.first_ts)
    ? fmtAtlasFirstTsHuman(p.first_ts)
    : "Date unknown";
  const html = `<div class="atlas-tl-spotlight-title">${escHtml(title)}</div><div class="atlas-tl-spotlight-date">${escHtml(dateLine)}</div>`;
  b2.innerHTML = html;
  b3.innerHTML = html;
  const show3d = atlasState?.mode === "3d" && atlasState?.has3d;
  w2.style.display = show3d ? "none" : "block";
  w3.style.display = show3d ? "block" : "none";
}

function tickAtlasTimelineSpotlightWindow() {
  if (!atlasTimelineState.engaged || !atlasTimelineState.spotlightVisible) return;
  const simNow = atlasTimelineState.simT;
  const prev = atlasTimelineState.spotlightLastSimT;
  const p = atlasTimelinePickBestInSimRange(prev, simNow);
  atlasTimelineState.spotlightLastSimT = simNow;
  if (p) renderAtlasTimelineSpotlightForPoint(p);
}

function refreshAtlasTimelineSpotlightOverall() {
  if (!atlasTimelineState.engaged || !atlasTimelineState.spotlightVisible) return;
  const p = atlasTimelinePickLatestAtOrBefore(atlasTimelineState.simT);
  if (p) renderAtlasTimelineSpotlightForPoint(p);
}

function syncAtlasTimelineControls() {
  const play = document.getElementById("atlas-tl-play");
  const pause = document.getElementById("atlas-tl-pause");
  const speedEl = document.getElementById("atlas-tl-speed");
  const timeEl = document.getElementById("atlas-tl-time");
  const hint = document.getElementById("atlas-tl-hint");
  const resetEl = document.getElementById("atlas-tl-reset");
  if (!play || !pause || !timeEl) return;
  const searchBlock =
    atlasState && atlasState.searchIds && atlasState.searchIds.size > 0;
  if (searchBlock) {
    hint.textContent = "Clear semantic search to use the timeline.";
  } else if (!atlasTimelineState.running && atlasTimelineState.engaged) {
    hint.textContent = "";
  }
  play.disabled =
    !!searchBlock || atlasTimelineState.running;
  pause.disabled =
    !!searchBlock || !atlasTimelineState.running;
  if (speedEl) speedEl.disabled = !!searchBlock;
  if (resetEl) resetEl.disabled = !!searchBlock;
  timeEl.textContent = atlasTimelineState.engaged
    ? fmtUtcDateTime(atlasTimelineState.simT)
    : "—";
}

function buildAtlasTimelineCache() {
  const visible = getAtlasVisiblePoints();
  const cr = atlasColorRangeForAtlasView();
  const baseCss = atlasYearColorsOnly(visible, cr.min, cr.max);
  const n = visible.length;
  const firstTs = [];
  const baseA = [];
  let tMin = Infinity;
  let tMax = -Infinity;
  for (let i = 0; i < n; i++) {
    const f = visible[i].first_ts;
    firstTs.push(atlasTimelineFirstTsValid(f) ? f : NaN);
    const rgba = atlasRgbaFromCss(baseCss[i]);
    baseA.push(rgba.a);
    if (atlasTimelineFirstTsValid(f)) {
      tMin = Math.min(tMin, f);
      tMax = Math.max(tMax, f);
    }
  }
  return { visible, baseCss, firstTs, baseA, tMin, tMax, n };
}

function applyAtlasTimelineFrame(simT, prevSimT) {
  if (!atlasTimelineState.engaged || !atlasTimelineState.firstTs) return;
  if (prevSimT !== undefined) {
    registerAtlasTimelineAppearances(prevSimT, simT);
  }
  const fts = atlasTimelineState.firstTs;
  const baseCss = atlasTimelineState.baseCss;
  const baseA = atlasTimelineState.baseA;
  const n = fts.length;

  if (
    atlasState.mode === "3d" &&
    atlasState.has3d &&
    atlas3dCtx &&
    atlas3dCtx.points
  ) {
    const vis3 = getAtlasVisiblePoints3d();
    if (vis3.length !== n) return;
    const geom = atlas3dCtx.points.geometry;
    const opAttr = geom.attributes.aOpacity;
    const sizeAttr = geom.attributes.aPointSize;
    if (!opAttr || opAttr.count !== n || !sizeAttr || sizeAttr.count !== n) return;
    const arr = opAttr.array;
    const baseSz = atlas3dPerVertexSizes(vis3);
    const now = performance.now();
    const P = ATLAS_TL_PULSE_MS;
    const pu = atlasTimelineState.pulseUntil;
    const szArr = sizeAttr.array;
    for (let i = 0; i < n; i++) {
      const tA = atlasTimelineAlphaAt(simT, fts[i]);
      let op = tA * baseA[i];
      let sz = baseSz[i];
      if (pu && pu[i] > now) {
        const u = 1 - (pu[i] - now) / P;
        const env = Math.sin(u * Math.PI);
        op = Math.min(1, op * (1 + 0.52 * env));
        sz *= 1 + 0.48 * env;
      }
      arr[i] = op;
      szArr[i] = sz;
    }
    opAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
    return;
  }

  if (!atlasChart || !atlasChart.atlasVisiblePoints) return;
  const pts = atlasChart.atlasVisiblePoints;
  if (pts.length !== n) return;
  const ds = atlasChart.data.datasets[0];
  if (!ds || !ds.data || ds.data.length !== n) return;
  if (atlasChart.getActiveElements().length > 0) {
    return;
  }
  const tip = atlasChart.tooltip;
  if (tip && typeof tip.opacity === "number" && tip.opacity > 0) {
    return;
  }
  const colors = new Array(n);
  for (let i = 0; i < n; i++) {
    const tA = atlasTimelineAlphaAt(simT, fts[i]);
    colors[i] = atlasCssWithTimelineAlpha(baseCss[i], tA);
  }
  ds.backgroundColor = colors;
  syncAtlasPointRadius(atlasChart);
}

function atlasTimelineLoop() {
  if (!atlasTimelineState.running) return;
  const wallMs = performance.now();
  const dt = (wallMs - atlasTimelineState.lastWallMs) / 1000;
  atlasTimelineState.lastWallMs = wallMs;
  const span = atlasTimelineState.tMax - atlasTimelineState.tMin;
  const durSec = ATLAS_TL_DURATION_1X_MS / 1000;
  const deltaSim =
    (span / durSec) * atlasTimelineState.speed * Math.max(0, dt);
  const prevST = atlasTimelineState.simT;
  let next = prevST + deltaSim;
  if (next >= atlasTimelineState.tMax) {
    next = atlasTimelineState.tMax;
    atlasTimelineState.simT = next;
    atlasTimelineState.running = false;
    applyAtlasTimelineFrame(next, prevST);
    stopAtlasTimelineSpotlightInterval();
    refreshAtlasTimelineSpotlightOverall();
    syncAtlasTimelineControls();
    atlasTimelineRaf = null;
    atlasMaybeStartPulseChartRaf();
    return;
  }
  atlasTimelineState.simT = next;
  applyAtlasTimelineFrame(next, prevST);
  syncAtlasTimelineControls();
  atlasTimelineRaf = requestAnimationFrame(atlasTimelineLoop);
}

function onAtlasTimelinePlay() {
  if (!atlasState) return;
  if (atlasState.searchIds && atlasState.searchIds.size > 0) return;
  if (atlasTimelineState.running) return;
  if (
    atlasTimelineState.engaged &&
    atlasTimelineState.firstTs &&
    atlasTimelineState.simT < atlasTimelineState.tMax - 1e-6
  ) {
    atlasTimelineState.speed = parseFloat(
      document.getElementById("atlas-tl-speed").value
    ) || 1;
    atlasTimelineState.lastWallMs = performance.now();
    cancelAtlasTimelinePulseChartRaf();
    atlasTimelineState.running = true;
    const vis = getAtlasVisiblePoints();
    if (vis.length !== atlasTimelineState.firstTs.length) {
      onAtlasTimelineReset();
      onAtlasTimelinePlay();
      return;
    }
    if (
      !atlasTimelineState.pulseUntil ||
      atlasTimelineState.pulseUntil.length !== vis.length
    ) {
      atlasTimelineState.pulseUntil = new Float64Array(vis.length);
    }
    syncAtlasTimelineControls();
    cancelAtlasTimelineRaf();
    startAtlasTimelineSpotlightInterval();
    atlasTimelineRaf = requestAnimationFrame(atlasTimelineLoop);
    return;
  }
  const c = buildAtlasTimelineCache();
  const hint = document.getElementById("atlas-tl-hint");
  if (!c.n || c.tMin === Infinity || c.tMax === -Infinity || c.tMax <= c.tMin) {
    if (hint) {
      hint.textContent =
        "No visible conversations with a valid first message time.";
    }
    return;
  }
  if (hint) hint.textContent = "";
  atlasTimelineState.engaged = true;
  atlasTimelineState.firstTs = c.firstTs;
  atlasTimelineState.baseCss = c.baseCss;
  atlasTimelineState.baseA = c.baseA;
  atlasTimelineState.spotlightVisible = c.visible.slice();
  atlasTimelineState.spotlightLastSimT = -Infinity;
  atlasTimelineState.tMin = c.tMin;
  atlasTimelineState.tMax = c.tMax;
  atlasTimelineState.simT = c.tMin;
  atlasTimelineState.speed = parseFloat(
    document.getElementById("atlas-tl-speed").value
  ) || 1;
  atlasTimelineState.lastWallMs = performance.now();
  cancelAtlasTimelinePulseChartRaf();
  atlasTimelineState.running = true;
  atlasTimelineState.pulseUntil = new Float64Array(c.n);
  applyAtlasTimelineFrame(atlasTimelineState.simT, atlasTimelineState.simT);
  tickAtlasTimelineSpotlightWindow();
  startAtlasTimelineSpotlightInterval();
  syncAtlasTimelineControls();
  cancelAtlasTimelineRaf();
  atlasTimelineRaf = requestAnimationFrame(atlasTimelineLoop);
}

function onAtlasTimelinePause() {
  if (!atlasTimelineState.running) return;
  atlasTimelineState.running = false;
  cancelAtlasTimelineRaf();
  stopAtlasTimelineSpotlightInterval();
  refreshAtlasTimelineSpotlightOverall();
  syncAtlasTimelineControls();
  atlasMaybeStartPulseChartRaf();
}

function onAtlasTimelineReset() {
  cancelAtlasTimelineRaf();
  teardownAtlasTimelineSpotlight();
  atlasTimelineState.engaged = false;
  atlasTimelineState.running = false;
  atlasTimelineState.firstTs = null;
  atlasTimelineState.baseCss = null;
  atlasTimelineState.baseA = null;
  atlasTimelineState.simT = 0;
  atlasTimelineState.tMin = 0;
  atlasTimelineState.tMax = 0;
  const hint = document.getElementById("atlas-tl-hint");
  if (hint) hint.textContent = "";
  paintAtlas(false);
  syncAtlasTimelineControls();
}

function stopAtlasTimelineForFilterChange() {
  if (!atlasTimelineState.engaged && !atlasTimelineState.running) return;
  cancelAtlasTimelineRaf();
  teardownAtlasTimelineSpotlight();
  atlasTimelineState.engaged = false;
  atlasTimelineState.running = false;
  atlasTimelineState.firstTs = null;
  atlasTimelineState.baseCss = null;
  atlasTimelineState.baseA = null;
  atlasTimelineState.simT = 0;
  atlasTimelineState.tMin = 0;
  atlasTimelineState.tMax = 0;
  const hint = document.getElementById("atlas-tl-hint");
  if (hint) hint.textContent = "";
  syncAtlasTimelineControls();
}

function stripAtlasTimelineForSearch() {
  cancelAtlasTimelineRaf();
  teardownAtlasTimelineSpotlight();
  atlasTimelineState.engaged = false;
  atlasTimelineState.running = false;
  atlasTimelineState.firstTs = null;
  atlasTimelineState.baseCss = null;
  atlasTimelineState.baseA = null;
  atlasTimelineState.simT = 0;
  atlasTimelineState.tMin = 0;
  atlasTimelineState.tMax = 0;
  syncAtlasTimelineControls();
}

function wireAtlasTimelineOnce() {
  if (atlasTimelineWired) return;
  atlasTimelineWired = true;
  document.getElementById("atlas-tl-play").addEventListener("click", onAtlasTimelinePlay);
  document.getElementById("atlas-tl-pause").addEventListener("click", onAtlasTimelinePause);
  document.getElementById("atlas-tl-reset").addEventListener("click", onAtlasTimelineReset);
  document.getElementById("atlas-tl-speed").addEventListener("change", (e) => {
    atlasTimelineState.speed = parseFloat(e.target.value) || 1;
  });
}

function boundsForPoints(points) {
  if (!points.length) return null;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    xMin: Math.min(...xs),
    xMax: Math.max(...xs),
    yMin: Math.min(...ys),
    yMax: Math.max(...ys)
  };
}

function updateAtlasMetaLine(visibleCount) {
  const total = atlasState.allPoints.length;
  const dim =
    atlasState.mode === "3d" && atlasState.has3d ? "UMAP 3D" : "UMAP 2D";
  const extra =
    " · cosine · year → hue · dot size ∝ √(messages in thread)";
  document.getElementById("atlas-meta").textContent =
    `${visibleCount} of ${total} shown · ${dim}${extra}`;
}

function atlasChartConfig() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      intersect: false,
      axis: "xy"
    },
    elements: {
      point: {
        pointStyle: "circle",
        borderWidth: 0
      }
    },
    onClick: (e, elements, chart) => {
      if (!elements.length) return;
      const idx = elements[0].index;
      const pts = chart.atlasVisiblePoints;
      const p = pts && pts[idx];
      if (!p || !p.conversation_id) return;
      openConversationModal(p.conversation_id);
    },
    plugins: {
      legend: { display: false },
      zoom: {
        limits: {
          x: { min: "original", max: "original" },
          y: { min: "original", max: "original" }
        },
        pan: {
          enabled: true,
          mode: "xy",
          onPanComplete: ({ chart }) => syncAtlasPointRadius(chart)
        },
        zoom: {
          wheel: {
            enabled: true,
            speed: 0.12
          },
          pinch: {
            enabled: true
          },
          mode: "xy",
          onZoomComplete: ({ chart }) => syncAtlasPointRadius(chart)
        }
      },
      tooltip: {
        bodyColor: "#e7ecf3",
        titleColor: "#c5d0de",
        backgroundColor: "#1a2332",
        borderColor: "#2a3545",
        borderWidth: 1,
        padding: 12,
        titleFont: { size: 14, weight: "600" },
        bodyFont: { size: 13, lineHeight: 1.4 },
        filter: () => true,
        callbacks: {
          label: (ctx) => {
            const pts = ctx.chart.atlasVisiblePoints;
            const p = pts && pts[ctx.dataIndex];
            return atlasPointTooltipLines(p);
          }
        }
      }
    },
    scales: {
      x: {
        type: "linear",
        title: {
          display: true,
          text: "UMAP 1",
          color: "#8b9cb3",
          font: { size: 14, weight: "500" }
        },
        ticks: { color: "#8b9cb3", font: { size: 13 } },
        grid: { color: "#2a3545" }
      },
      y: {
        type: "linear",
        title: {
          display: true,
          text: "UMAP 2",
          color: "#8b9cb3",
          font: { size: 14, weight: "500" }
        },
        ticks: { color: "#8b9cb3", font: { size: 13 } },
        grid: { color: "#2a3545" }
      }
    }
  };
}

function paintAtlas(resetZoom) {
  if (!atlasState) return;
  if (atlasState.mode === "3d" && atlasState.has3d) {
    void paintAtlas3d(resetZoom);
    return;
  }
  paintAtlas2d(resetZoom);
}

function paintAtlas2d(resetZoom) {
  if (!atlasState) return;
  const visible = getAtlasVisiblePoints();
  updateAtlasMetaLine(visible.length);
  if (visible.length === 0) {
    const fallbackBounds = boundsForPoints(atlasState.allPoints);
    const b =
      fallbackBounds || { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };
    if (!atlasChart) {
      atlasChart = new Chart(document.getElementById("chart-atlas"), {
        type: "scatter",
        data: {
          datasets: [
            {
              label: "Conversations",
              data: [],
              backgroundColor: [],
              pointRadius: 5,
              pointHoverRadius: 11
            }
          ]
        },
        options: atlasChartConfig()
      });
    } else {
      const ds = atlasChart.data.datasets[0];
      ds.data = [];
      ds.backgroundColor = [];
      if (resetZoom) atlasChart.resetZoom();
      atlasChart.update("none");
    }
    atlasChart.atlasShowHulls = false;
    atlasChart.atlasHullRings = [];
    atlasChart.atlasDataBounds = b;
    atlasChart.atlasVisiblePoints = [];
    return;
  }
  const cr = atlasColorRangeForAtlasView();
  const colors = atlasColorsForPoints(visible, cr.min, cr.max);
  let dataBounds = boundsForPoints(visible);
  if (!dataBounds) dataBounds = boundsForPoints(atlasState.allPoints);

  const canvas = document.getElementById("chart-atlas");
  if (!atlasChart) {
    atlasChart = new Chart(canvas, {
      type: "scatter",
      data: {
        datasets: [
          {
            label: "Conversations",
            data: visible.map((p) => ({ x: p.x, y: p.y })),
            backgroundColor: colors,
            pointRadius: 5,
            pointHoverRadius: 11
          }
        ]
      },
      options: atlasChartConfig()
    });
  } else {
    const ds = atlasChart.data.datasets[0];
    ds.data = visible.map((p) => ({ x: p.x, y: p.y }));
    ds.backgroundColor = colors;
    if (resetZoom) atlasChart.resetZoom();
    atlasChart.update("none");
  }
  atlasChart.atlasDataBounds = dataBounds;
  atlasChart.atlasVisiblePoints = visible;
  const hullEl = document.getElementById("atlas-hulls");
  const showHulls =
    atlasState.clusteringEnabled &&
    hullEl &&
    hullEl.checked;
  atlasChart.atlasShowHulls = !!showHulls;
  atlasChart.atlasHullRings = showHulls
    ? buildClusterHullRings(visible, dataBounds)
    : [];
  requestAnimationFrame(() => {
    requestAnimationFrame(() => syncAtlasPointRadius(atlasChart));
  });
}

let atlas3dRaf = null;
let atlas3dResizeBound = false;

function stopAtlas3dLoop() {
  if (atlas3dRaf) {
    cancelAnimationFrame(atlas3dRaf);
    atlas3dRaf = null;
  }
}

function startAtlas3dLoop() {
  if (atlas3dRaf) return;
  function loop() {
    atlas3dRaf = requestAnimationFrame(loop);
    if (!atlas3dCtx || atlasState?.mode !== "3d") {
      cancelAnimationFrame(atlas3dRaf);
      atlas3dRaf = null;
      return;
    }
    if (
      atlasTimelineState.engaged &&
      !atlasTimelineState.running &&
      atlasHasActiveTimelinePulse() &&
      atlas3dCtx.points
    ) {
      applyAtlasTimelineFrame(atlasTimelineState.simT);
    }
    atlas3dCtx.controls.update();
    const pr = atlas3dCtx.renderer.getPixelRatio();
    const h = atlas3dCtx.renderer.domElement.height;
    const mat = atlas3dCtx.points?.material;
    if (mat?.uniforms?.uScale) {
      mat.uniforms.uScale.value = h * 0.5 * pr;
    }
    atlas3dCtx.renderer.render(atlas3dCtx.scene, atlas3dCtx.camera);
  }
  atlas3dRaf = requestAnimationFrame(loop);
}

function onAtlas3dResize() {
  if (!atlas3dCtx || atlasState?.mode !== "3d") return;
  const wrap = document.getElementById("atlas-3d-wrap");
  if (!wrap || wrap.style.display === "none") return;
  const w = Math.max(1, wrap.clientWidth);
  const h = Math.max(1, wrap.clientHeight);
  const { camera, renderer, scene } = atlas3dCtx;
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
  renderer.render(scene, camera);
}

function onAtlas3dPointerDown(ev) {
  if (!atlas3dCtx || atlasState?.mode !== "3d") return;
  const canvas = document.getElementById("atlas-3d-canvas");
  if (!canvas || ev.button !== 0) return;
  if (!atlas3dCtx.points || !atlas3dCtx.conversationIds.length) return;
  const idx = atlas3dPickIndexFromPointer(ev, canvas);
  if (idx < 0) return;
  openConversationModal(atlas3dCtx.conversationIds[idx]);
}

function onAtlas3dPointerMove(ev) {
  if (!atlas3dCtx || atlasState?.mode !== "3d") return;
  const canvas = document.getElementById("atlas-3d-canvas");
  const tip = document.getElementById("atlas-3d-tooltip");
  if (!canvas || !tip) return;
  const { points, atlas3dHoverPoints } = atlas3dCtx;
  if (!points || !atlas3dHoverPoints || !atlas3dHoverPoints.length) {
    hideAtlas3dTooltip();
    return;
  }
  const idx = atlas3dPickIndexFromPointer(ev, canvas);
  if (idx < 0) {
    hideAtlas3dTooltip();
    return;
  }
  const p = atlas3dHoverPoints[idx];
  if (!p) {
    hideAtlas3dTooltip();
    return;
  }
  const lines = atlasPointTooltipLines(p);
  if (!lines.length) {
    hideAtlas3dTooltip();
    return;
  }
  if (idx !== atlas3dCtx.lastHoverIdx) {
    atlas3dCtx.lastHoverIdx = idx;
    tip.innerHTML = lines
      .map((line, i) =>
        i === 0
          ? `<div class="atlas-3d-tooltip-line atlas-3d-tooltip-title">${escHtml(line)}</div>`
          : `<div class="atlas-3d-tooltip-line">${escHtml(line)}</div>`
      )
      .join("");
  }
  tip.style.display = "block";
  tip.setAttribute("aria-hidden", "false");
  requestAnimationFrame(() => positionAtlas3dTooltip(tip, ev));
}

function onAtlas3dPointerLeave() {
  hideAtlas3dTooltip();
}

function disposeAtlas3d() {
  stopAtlas3dLoop();
  hideAtlas3dTooltip();
  if (!atlas3dCtx) return;
  const { scene, renderer, controls, points } = atlas3dCtx;
  if (points) {
    scene.remove(points);
    points.geometry.dispose();
    const m = points.material;
    if (m) {
      if (m.uniforms?.pointTexture?.value) m.uniforms.pointTexture.value = null;
      if (m.map) m.map = null;
      m.dispose();
    }
  }
  controls.dispose();
  renderer.dispose();
  atlas3dCtx = null;
  if (atlas3dCircleTex) {
    atlas3dCircleTex.dispose();
    atlas3dCircleTex = null;
  }
}

async function ensureAtlas3d() {
  if (atlas3dCtx) return atlas3dCtx;
  const THREE = await import("three");
  const { OrbitControls } = await import(
    "three/examples/jsm/controls/OrbitControls.js"
  );
  const canvas = document.getElementById("atlas-3d-canvas");
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0f1419);
  const camera = new THREE.PerspectiveCamera(55, 1, 0.001, 1e6);
  camera.position.set(4, 3, 5);
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  atlas3dCtx = {
    THREE,
    scene,
    camera,
    renderer,
    controls,
    points: null,
    conversationIds: [],
    atlas3dHoverPoints: [],
    lastHoverIdx: -1
  };
  if (!atlas3dPointerBound) {
    atlas3dPointerBound = true;
    canvas.addEventListener("pointerdown", onAtlas3dPointerDown);
  }
  if (!atlas3dHoverBound) {
    atlas3dHoverBound = true;
    canvas.addEventListener("pointermove", onAtlas3dPointerMove);
    canvas.addEventListener("pointerleave", onAtlas3dPointerLeave);
  }
  if (!atlas3dResizeBound) {
    atlas3dResizeBound = true;
    window.addEventListener("resize", onAtlas3dResize);
  }
  onAtlas3dResize();
  return atlas3dCtx;
}

async function paintAtlas3d(resetCamera) {
  if (!atlasState || !atlasState.has3d) return;
  const visible = getAtlasVisiblePoints3d();
  updateAtlasMetaLine(visible.length);
  const ctx = await ensureAtlas3d();
  const THREE = ctx.THREE;
  const { scene, camera, renderer, controls } = ctx;

  if (!visible.length) {
    hideAtlas3dTooltip();
    if (ctx.points) {
      scene.remove(ctx.points);
      ctx.points.geometry.dispose();
      const m = ctx.points.material;
      if (m) {
        if (m.uniforms?.pointTexture?.value) m.uniforms.pointTexture.value = null;
        if (m.map) m.map = null;
        m.dispose();
      }
      ctx.points = null;
    }
    ctx.conversationIds = [];
    ctx.atlas3dHoverPoints = [];
    ctx.lastHoverIdx = -1;
    renderer.render(scene, camera);
    return;
  }

  ctx.atlas3dHoverPoints = visible;
  ctx.lastHoverIdx = -1;

  const cr = atlasColorRangeForAtlasView();
  const cssColors = atlasColorsForPoints(visible, cr.min, cr.max);
  const n = visible.length;
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const opa = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const p = visible[i];
    pos[i * 3] = p.x;
    pos[i * 3 + 1] = p.y;
    pos[i * 3 + 2] = p.z;
    const rgba = atlasRgbaFromCss(cssColors[i]);
    col[i * 3] = rgba.r;
    col[i * 3 + 1] = rgba.g;
    col[i * 3 + 2] = rgba.b;
    opa[i] = rgba.a;
  }

  const box = new THREE.Box3();
  for (let i = 0; i < n; i++) {
    box.expandByPoint(
      new THREE.Vector3(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2])
    );
  }
  const center = new THREE.Vector3();
  box.getCenter(center);
  const sx = box.max.x - box.min.x;
  const sy = box.max.y - box.min.y;
  const sz = box.max.z - box.min.z;
  const maxDim = Math.max(sx, sy, sz, 1e-9);
  for (let i = 0; i < n; i++) {
    pos[i * 3] -= center.x;
    pos[i * 3 + 1] -= center.y;
    pos[i * 3 + 2] -= center.z;
  }

  ctx.conversationIds = visible.map((p) => p.conversation_id);

  const sizes = atlas3dPerVertexSizes(visible);
  if (atlasState.searchIds && atlasState.searchIds.size > 0 && atlasState.searchStrength) {
    for (let i = 0; i < n; i++) {
      const cid = visible[i].conversation_id;
      if (atlasState.searchIds.has(cid)) {
        sizes[i] *= atlasSearchBoostForConversationId(cid);
      }
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geom.setAttribute("color", new THREE.BufferAttribute(col, 3));
  geom.setAttribute("aOpacity", new THREE.BufferAttribute(opa, 1));
  geom.setAttribute("aPointSize", new THREE.BufferAttribute(sizes, 1));

  const tex = getAtlas3dCircleTexture(THREE);
  if (ctx.points) {
    ctx.points.geometry.dispose();
    ctx.points.geometry = geom;
    if (ctx.points.material.type !== "ShaderMaterial") {
      ctx.points.material.dispose();
      ctx.points.material = createAtlas3dPointsMaterial(THREE, tex);
    }
  } else {
    ctx.points = new THREE.Points(
      geom,
      createAtlas3dPointsMaterial(THREE, tex)
    );
    scene.add(ctx.points);
  }
  const pr = renderer.getPixelRatio();
  const hh = renderer.domElement.height;
  ctx.points.material.uniforms.uScale.value = hh * 0.5 * pr;

  if (resetCamera) {
    const dist = maxDim * 1.5;
    camera.position.set(dist * 0.9, dist * 0.65, dist * 1.05);
    controls.target.set(0, 0, 0);
    camera.near = Math.max(maxDim * 1e-4, 1e-6);
    camera.far = Math.max(maxDim * 100, 100);
    camera.updateProjectionMatrix();
    controls.update();
  }
  renderer.render(scene, camera);
}

function setAtlasMode(mode) {
  if (!atlasState || !atlasState.has3d) return;
  if (mode !== "2d" && mode !== "3d") return;
  atlasState.mode = mode;
  const w2 = document.getElementById("atlas-2d-wrap");
  const w3 = document.getElementById("atlas-3d-wrap");
  const btn2 = document.getElementById("atlas-mode-2d");
  const btn3 = document.getElementById("atlas-mode-3d");
  const hint = document.getElementById("atlas-zoom-hint");
  if (mode === "3d") {
    w2.style.display = "none";
    w3.style.display = "block";
    if (btn2) btn2.setAttribute("aria-pressed", "false");
    if (btn3) btn3.setAttribute("aria-pressed", "true");
    if (hint) {
      hint.textContent =
        " · Drag to orbit · scroll to zoom · right-drag to pan · click a point for full thread";
    }
    void paintAtlas3d(true).then(() => {
      requestAnimationFrame(() => {
        onAtlas3dResize();
        startAtlas3dLoop();
        if (atlasTimelineState.engaged) {
          applyAtlasTimelineFrame(atlasTimelineState.simT);
          refreshAtlasTimelineSpotlightOverall();
        }
      });
    });
  } else {
    w3.style.display = "none";
    w2.style.display = "block";
    if (btn2) btn2.setAttribute("aria-pressed", "true");
    if (btn3) btn3.setAttribute("aria-pressed", "false");
    if (hint) {
      hint.textContent =
        " · Scroll or pinch to zoom · drag to pan · click a point for full thread";
    }
    stopAtlas3dLoop();
    hideAtlas3dTooltip();
    paintAtlas2d(false);
    if (atlasTimelineState.engaged) {
      requestAnimationFrame(() => {
        applyAtlasTimelineFrame(atlasTimelineState.simT);
        refreshAtlasTimelineSpotlightOverall();
      });
    }
  }
}

function wireAtlasViewToggle() {
  const b2 = document.getElementById("atlas-mode-2d");
  const b3 = document.getElementById("atlas-mode-3d");
  if (!b2 || !b3 || b2.dataset.wired) return;
  b2.dataset.wired = "1";
  b2.addEventListener("click", () => setAtlasMode("2d"));
  b3.addEventListener("click", () => setAtlasMode("3d"));
}

async function applyAtlasSemanticSearch(query, opts) {
  const updateInput = opts && opts.updateInput;
  const q = String(query || "").trim();
  const status = document.getElementById("atlas-search-status");
  const k = Math.min(
    50,
    Math.max(1, parseInt(document.getElementById("atlas-k").value, 10) || 15)
  );
  if (!atlasState) return;
  if (!q) {
    atlasState.searchIds = null;
    atlasState.searchStrength = null;
    if (status) status.textContent = "";
    paintAtlas(false);
    syncAtlasTimelineControls();
    return;
  }
  if (updateInput) {
    const inp = document.getElementById("atlas-q");
    if (inp) inp.value = q;
  }
  if (status) status.textContent = "Searching…";
  try {
    const res = await apiFetch(`/api/search?q=${encodeURIComponent(q)}&k=${k}`);
    const raw = await res.text();
    let j;
    try {
      j = JSON.parse(raw);
    } catch {
      throw new Error(atlasHelp());
    }
    if (!j.ok) throw new Error(j.error || `HTTP ${res.status}`);
    stripAtlasTimelineForSearch();
    atlasState.searchIds = new Set(j.results.map((r) => r.conversation_id));
    atlasState.searchStrength = buildAtlasSearchStrengthMap(j.results);
    if (status) {
      status.textContent = `${j.results.length} matches (brighter and larger = stronger hybrid score)`;
    }
    paintAtlas(false);
    syncAtlasTimelineControls();
  } catch (e) {
    if (status) status.textContent = String(e.message || e);
    atlasState.searchIds = null;
    atlasState.searchStrength = null;
    paintAtlas(false);
    syncAtlasTimelineControls();
  }
}

async function runAtlasSearch() {
  const q = document.getElementById("atlas-q").value.trim();
  await applyAtlasSemanticSearch(q, { updateInput: false });
}

function clearAtlasSearch() {
  if (!atlasState) return;
  atlasState.searchIds = null;
  atlasState.searchStrength = null;
  document.getElementById("atlas-q").value = "";
  document.getElementById("atlas-search-status").textContent = "";
  paintAtlas(false);
  syncAtlasTimelineControls();
}

async function checkAtlasBackend() {
  const el = document.getElementById("atlas-serve-hint");
  if (!el) return;
  if (!getApiBase()) {
    el.style.display = "block";
    el.textContent = atlasHelp();
    return;
  }
  try {
    const r = await apiFetch("/api/health", { cache: "no-store" });
    const raw = await r.text();
    const j = JSON.parse(raw);
    const okServer =
      j &&
      j.ok === true &&
      (j.server === "serve_dashboard" || j.server === "usage_dashboard_api");
    if (okServer) {
      el.style.display = "none";
      el.textContent = "";
      return;
    }
  } catch (_) {
    /* wrong server or offline */
  }
  el.style.display = "block";
  el.textContent =
    atlasHelp();
}

function wireAtlasToolbarOnce() {
  if (atlasToolbarWired) return;
  atlasToolbarWired = true;
  ["atlas-y-min", "atlas-y-max", "atlas-unknown"].forEach((id) => {
    const el = document.getElementById(id);
    const onF = () => {
      stopAtlasTimelineForFilterChange();
      paintAtlas(true);
    };
    el.addEventListener("input", onF);
    el.addEventListener("change", onF);
  });
  document.getElementById("atlas-search-btn").addEventListener("click", () => runAtlasSearch());
  document.getElementById("atlas-clear-search").addEventListener("click", () => clearAtlasSearch());
  document.getElementById("atlas-q").addEventListener("keydown", (e) => {
    if (e.key === "Enter") runAtlasSearch();
  });
  document.getElementById("atlas-reset-zoom").onclick = () => {
    if (atlasState?.mode === "3d" && atlasState?.has3d) {
      void paintAtlas3d(true);
      return;
    }
    if (atlasChart) {
      atlasChart.resetZoom();
      requestAnimationFrame(() => syncAtlasPointRadius(atlasChart));
    }
  };
  const hullCb = document.getElementById("atlas-hulls");
  if (hullCb) {
    hullCb.addEventListener("change", () => {
      stopAtlasTimelineForFilterChange();
      paintAtlas(false);
    });
  }
}

function initAtlas(embed, embed3d) {
  const pts = embed.points;
  if (!pts || pts.length === 0) return;
  cancelAtlasTimelineRaf();
  teardownAtlasTimelineSpotlight();
  atlasTimelineState.engaged = false;
  atlasTimelineState.running = false;
  atlasTimelineState.firstTs = null;
  atlasTimelineState.baseCss = null;
  atlasTimelineState.baseA = null;
  atlasTimelineState.simT = 0;
  atlasTimelineState.tMin = 0;
  atlasTimelineState.tMax = 0;
  disposeAtlas3d();
  const has3d =
    embed3d &&
    embed3d.points &&
    embed3d.points.length === pts.length;
  const pts3d = has3d ? embed3d.points : null;
  const hasClusters = pts.some((p) => typeof p.cluster_id === "number");
  const hullWrap = document.getElementById("atlas-hulls-wrap");
  if (hullWrap) hullWrap.style.display = hasClusters ? "inline" : "none";
  const ys = pts.map((p) => p.year).filter((y) => y != null);
  const yearMin = ys.length ? Math.min(...ys) : new Date().getFullYear();
  const yearMax = ys.length ? Math.max(...ys) : new Date().getFullYear();
  const cl =
    embed.meta &&
    embed.meta.clustering &&
    embed.meta.clustering.cluster_labels;
  atlasState = {
    allPoints: pts,
    allPoints3d: pts3d,
    has3d: !!pts3d,
    mode: "2d",
    metaN: embed.meta && embed.meta.n != null ? embed.meta.n : pts.length,
    yearMin,
    yearMax,
    searchIds: null,
    searchStrength: null,
    clusteringEnabled: hasClusters,
    clusterLabels: cl && typeof cl === "object" ? cl : {}
  };
  const toggleRow = document.getElementById("atlas-view-toggle-row");
  if (toggleRow) {
    toggleRow.style.display = atlasState.has3d ? "flex" : "none";
  }
  const w2 = document.getElementById("atlas-2d-wrap");
  const w3 = document.getElementById("atlas-3d-wrap");
  if (w2) w2.style.display = "block";
  if (w3) w3.style.display = "none";
  const btn2 = document.getElementById("atlas-mode-2d");
  const btn3 = document.getElementById("atlas-mode-3d");
  if (btn2) btn2.setAttribute("aria-pressed", "true");
  if (btn3) btn3.setAttribute("aria-pressed", "false");
  document.getElementById("section-atlas").style.display = "block";
  document.getElementById("atlas-toolbar").style.display = "flex";
  const tlBar = document.getElementById("atlas-timeline-bar");
  if (tlBar) tlBar.style.display = "flex";
  document.getElementById("atlas-y-min").value = yearMin;
  document.getElementById("atlas-y-max").value = yearMax;
  document.getElementById("atlas-search-status").textContent = "";
  wireAtlasToolbarOnce();
  wireAtlasTimelineOnce();
  wireAtlasViewToggle();
  syncAtlasTimelineControls();
  if (atlasChart) {
    atlasChart.destroy();
    atlasChart = null;
  }
  paintAtlas(true);
  checkAtlasBackend();
}

function buildCharts(data, embed2d, embed3d, includeUsageStats) {
  const s = data.summary;
  if (includeUsageStats) {
    document.getElementById("subtitle").textContent =
      `${fmtDate(s.first_message_utc)} → ${fmtDate(s.last_message_utc)} · TZ: ${s.timezone} · data.json + ${data.meta.generated_at.slice(0, 10)}`;
    addCards(s);
    renderLongestChatHero(data.longest_conversations);
  } else {
    document.getElementById("subtitle").textContent =
      "Memorial assistant & conversation atlas";
  }
  if (embed2d && embed2d.points && embed2d.points.length) {
    initAtlas(embed2d, embed3d);
  }
  if (!includeUsageStats) return;

  const monthly = data.monthly;
  const labelsM = monthly.map((r) => r.month);

  new Chart(document.getElementById("chart-monthly"), {
    type: "bar",
    data: {
      labels: labelsM,
      datasets: [{ label: "Messages", data: monthly.map((r) => r.messages), backgroundColor: "#5b9fd4aa" }]
    },
    options: { ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }
  });

  new Chart(document.getElementById("chart-cumulative"), {
    type: "line",
    data: {
      labels: data.cumulative_by_month.map((r) => r.month),
      datasets: [{
        label: "Cumulative messages",
        data: data.cumulative_by_month.map((r) => r.cumulative_messages),
        borderColor: "#8bc34a",
        backgroundColor: "#8bc34a33",
        fill: true,
        tension: 0.1
      }]
    },
    options: chartDefaults
  });

  new Chart(document.getElementById("chart-newconv"), {
    type: "bar",
    data: {
      labels: labelsM,
      datasets: [{ label: "New conversations", data: monthly.map((r) => r.new_conversations), backgroundColor: "#ce93d899" }]
    },
    options: { ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }
  });

  const buckets = data.messages_per_conversation_buckets;
  new Chart(document.getElementById("chart-buckets"), {
    type: "bar",
    data: {
      labels: buckets.map((b) => b.bucket),
      datasets: [{ label: "Conversations", data: buckets.map((b) => b.count), backgroundColor: "#81c78499" }]
    },
    options: { ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }
  });

  new Chart(document.getElementById("chart-hour"), {
    type: "bar",
    data: {
      labels: data.hour_of_day.map((h) => String(h.hour)),
      datasets: [{ label: "Messages", data: data.hour_of_day.map((h) => h.count), backgroundColor: "#ffb74d99" }]
    },
    options: { ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }
  });

  new Chart(document.getElementById("chart-weekday"), {
    type: "bar",
    data: {
      labels: data.weekday.map((w) => w.weekday),
      datasets: [{ label: "Messages", data: data.weekday.map((w) => w.count), backgroundColor: "#4dd0e199" }]
    },
    options: { ...chartDefaults, plugins: { ...chartDefaults.plugins, legend: { display: false } } }
  });

  const roll = data.rolling_7d;
  new Chart(document.getElementById("chart-rolling"), {
    type: "line",
    data: {
      labels: roll.map((r) => r.date),
      datasets: [{
        label: "7-day sum",
        data: roll.map((r) => r.messages_7d),
        borderColor: "#ef9a9a",
        backgroundColor: "#ef9a9a33",
        fill: true,
        tension: 0.15,
        pointRadius: 0
      }]
    },
    options: chartDefaults
  });

  const tw = data.top_title_words;
  new Chart(document.getElementById("chart-words"), {
    type: "bar",
    data: {
      labels: tw.map((w) => w.word),
      datasets: [
        {
          label: "Count",
          data: tw.map((w) => Number(w.count)),
          backgroundColor: "rgba(144, 202, 249, 0.85)",
          borderColor: "rgba(144, 202, 249, 1)",
          borderWidth: 1,
          borderSkipped: false
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      plugins: { ...chartDefaults.plugins, legend: { display: false } },
      scales: {
        x: {
          type: "linear",
          beginAtZero: true,
          grid: { color: "#2a3545" },
          ticks: { color: "#8b9cb3" }
        },
        y: {
          type: "category",
          grid: { display: false },
          ticks: { color: "#8b9cb3", autoSkip: false }
        }
      }
    }
  });

  const tbody = document.querySelector("#table-long tbody");
  tbody.replaceChildren();
  for (const row of data.longest_conversations) {
    const tr = document.createElement("tr");
    const tdTitle = document.createElement("td");
    const rawTitle = row.title || "";
    const titleDisplay =
      rawTitle.slice(0, 120) + (rawTitle.length > 120 ? "…" : "");
    const cid = row.conversation_id || "";
    if (cid) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "table-long-title";
      btn.textContent = titleDisplay;
      btn.title = "Open full thread";
      btn.addEventListener("click", () => openConversationModal(cid));
      tdTitle.appendChild(btn);
    } else {
      tdTitle.textContent = titleDisplay;
    }
    const tdMsgs = document.createElement("td");
    tdMsgs.textContent = String(row.message_count);
    const tdChars = document.createElement("td");
    tdChars.textContent = String(row.total_chars);
    tr.append(tdTitle, tdMsgs, tdChars);
    tbody.appendChild(tr);
  }
}

async function load() {
  await tryLocalBootstrap();
  updateChatSectionVisibility();
  const gate = document.getElementById("auth-gate");
  if (authGateVisible()) {
    if (gate) gate.style.display = "flex";
    document.getElementById("app").style.display = "none";
    document.getElementById("subtitle").textContent = "Sign in required";
    return;
  }
  if (gate) gate.style.display = "none";
  updateSignOutVisibility();
  try {
    const res = await apiFetch("data.json", { cache: "no-store" });
    if (res.status === 401) {
      sessionStorage.removeItem(USAGE_DASH_TOKEN_KEY);
      if (gate) gate.style.display = "flex";
      document.getElementById("app").style.display = "none";
      document.getElementById("subtitle").textContent = "Session expired";
      updateChatSectionVisibility();
      return;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    let embed2d = null;
    let embed3d = null;
    try {
      const er = await apiFetch("embed_2d.json", { cache: "no-store" });
      if (er.ok) embed2d = await er.json();
    } catch (_) {
      /* optional */
    }
    try {
      const e3 = await apiFetch("embed_3d.json", { cache: "no-store" });
      if (e3.ok) embed3d = await e3.json();
    } catch (_) {
      /* optional */
    }
    await refreshMe();
    const includeUsageStats = shouldShowUsageStatsGrid(viewerEmail);
    document.getElementById("app").style.display = includeUsageStats ? "grid" : "none";
    buildCharts(data, embed2d, embed3d, includeUsageStats);
  } catch (e) {
    document.getElementById("load-error").style.display = "block";
    document.getElementById("load-error").textContent =
      "Could not load data.json. " + atlasHelp();
    console.error(e);
  }
}
consumeAuthHash();
wireAuthGate();
updateSignOutVisibility();
updateChatSectionVisibility();
wireChat();
wireConversationDialog();
load();